// src/pages/TasksPage/TasksPage.tsx
import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  ConfigProvider,
  DatePicker,
  Input,
  Popconfirm,
  Popover,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
} from "antd";
import {
  BellOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  SwapOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import esES from "antd/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "./tareas.css";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useTasksLogic } from "./useTasksLogic";
import { ManageColor } from "./manageColor";
import ImageEditorModal from "../../Components/ImageEditorModal";
import DocsModal from "./components/DocsModal";
import ImagesGalleryModal from "./components/ImagesGalleryModal";
import RecurrenciaModal from "./components/RecurrenciaModal";
import AlertasDrawer from "./components/AlertasDrawer";
import type { Recurrencia } from "./types";

import {
  Id,
  Task,
  NewTask,
  columnsTareasDefault,
  ColumnDef,
  SpaceMode,
  ESTADOS_CONSULTA,
  ESTADOS_SECTOR,
  PRIORIDADES_TAREA,
} from "./types";
import {
  CameraOutlined,
  CheckOutlined,
  FileOutlined,
} from "@ant-design/icons";
import type { DocumentMeta } from "../../services/tareas.api";
import { isAdminRole } from "../../utils/roles.utils";
import { norm } from "../../utils/fuzzyMatch";
import { compressImageFile, dataUrlToFile } from "../../utils/imageCompression";
import { downloadFile, downloadFilesAsZip } from "../../utils/downloadHelpers";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg, getModal } from "../../utils/antdStatic";
import { ahoraChile } from "../../utils/fechaChile";
import { paginacionCliente } from "../../utils/tablaPaginacion";
import {
  useFiltroPersistido,
  useRangoPersistido,
} from "../../hooks/useFiltroPersistido";

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB — límite cliente para imágenes/videos

dayjs.locale("es");

type DraftRow = Task & { __draft?: true };

const ALL_COLUMNS: { label: string; key: ColumnDef["key"] }[] = [
  { label: "Descripción", key: "descripcion" },
  { label: "Propiedades", key: "propiedadIds" },
  { label: "Trabajador", key: "trabajadorId" },
  { label: "Prioridad", key: "prioridad" },
  { label: "Estado", key: "estado" },
  { label: "Registrado el", key: "registradoEl" },
  { label: "Terminado el", key: "terminadoEl" },
  { label: "Costo", key: "costo" },
  { label: "Imágenes", key: "imagenes" },
  { label: "Permitir ver a cliente", key: "verCliente" },
];

const uid = (): Id =>
  crypto?.randomUUID?.() ?? `id_${Math.random().toString(16).slice(2)}`;

export default function TasksPage() {
  const store = useTasksLogic();
  // Defaults aplicados a cualquier draft nuevo en /tareas (board y list).
  // - `registradoEl` y `fecha` caen a hoy: cubre las dos formas que la UI usa
  //   para "fecha de registro" según el space (board usa registradoEl; los
  //   spaces system "Consultas" y "Gerencia" usan `fecha`). Memoizado con
  //   deps vacías → la cadena se calcula al montar la página; aceptable
  //   porque navegar a /tareas en otro día genera un mount nuevo.
  // - `estado` cae a "registrado" — el primer valor de ESTADOS_SECTOR — para
  //   que las nuevas tareas board entren al kanban con un estado coherente
  //   (antes quedaban en null y la columna mostraba "—" hasta que el usuario
  //   lo seteara a mano). Editable como cualquier otro campo.
  // Round 2026-05-05: el usuario pidió defaults consistentes en todas las
  // opciones de /tareas.
  const emptyDraft = useMemo(
    () => ({
      propiedadIds: [] as Number[],
      trabajadorId: null as number | null,
      prioridad: null as string | null,
      estado: "registrado" as string | null,
      registradoEl: ahoraChile().format("YYYY-MM-DD"),
      terminadoEl: null,
      costo: null,
      telefono: undefined,
      nombre: undefined,
      estadoConsulta: "" as string | undefined,
      fecha: ahoraChile().format("YYYY-MM-DD") as string | undefined,
      ubicacion: undefined,
      descripcion: "" as string | undefined,
      importancia: undefined,
      verCliente: false,
    }),
    [],
  );
  const { db, tasks, loading, activeSpace, user } = store;
  const activeCategory = useMemo(
    () =>
      activeSpace?.categories?.find(
        (c: any) => c.id === store.activeCategoryId,
      ) ?? null,
    [activeSpace, store.activeCategoryId],
  );
  // setEditingItemId no se usa actualmente; el id solo lo lee el render.
  const [editingItemId] = useState<string | null>(null);
  const [editingSpaceId, setEditingSpaceId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  // Edición inline de categorías (board mode). Mismo patrón que espacios:
  // doble click → input controlado, onBlur o Enter persisten. El borrado
  // pide confirmación en `askDeleteCategory` por debajo.
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState<string>("");
  // Mientras corre el PATCH del rename, mostramos un Spin sobre el item de
  // la categoría afectada para feedback visual (la network round-trip puede
  // tardar 200-500 ms y sin spinner el usuario duda si quedó guardado).
  // Almacenamos el ID de la categoría en transición.
  const [renamingCategoryId, setRenamingCategoryId] = useState<string | null>(null);
  // Modal de recurrencia. `recurrenciaTask` apunta a la tarea editada (su
  // shape ya viene del DB). `null` = modal cerrado. Mientras `recurrenciaSaving`
  // está true, el botón Guardar muestra spinner y bloquea cierre prematuro.
  const [recurrenciaTask, setRecurrenciaTask] = useState<Task | null>(null);
  const [recurrenciaSaving, setRecurrenciaSaving] = useState(false);

  // Drawer de alertas (round 2026-05-06). Se abre con el botón 🔔 arriba
  // de la tabla. Cargamos las alertas perezosamente — solo cuando el
  // usuario abre el drawer — para no pegarle al backend en cada render.
  const [alertasOpen, setAlertasOpen] = useState(false);
  const [alertasLoading, setAlertasLoading] = useState(false);
  const [alertasVencidas, setAlertasVencidas] = useState<Task[]>([]);
  const [alertasRecurrentes, setAlertasRecurrentes] = useState<Task[]>([]);
  // Contador para el badge sobre el icono — refrescado en cada apertura.
  const alertasTotal =
    (alertasVencidas?.length ?? 0) + (alertasRecurrentes?.length ?? 0);

  const fetchAlertas = async () => {
    setAlertasLoading(true);
    try {
      const data = await store.getAlertas();
      setAlertasVencidas(data.vencidas || []);
      setAlertasRecurrentes(data.recurrentesProximas || []);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error cargando alertas");
    } finally {
      setAlertasLoading(false);
    }
  };

  // Al abrir el drawer recargamos. Mantenemos el último resultado mientras
  // está cerrado para que el badge contador del botón siga reflejando algo
  // en pantallas largas.
  useEffect(() => {
    if (alertasOpen) {
      fetchAlertas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertasOpen]);

  // Carga inicial al montar para popular el badge sin que el usuario tenga
  // que abrir el drawer. El backend ya filtra por rol, así que corre para
  // todos. Antes el guard exigía `Array.isArray(user.tipo)`, pero `tipo` es
  // un string escalar ("administrador" | "aseador" | "cliente") → nunca era
  // array, así que el badge quedaba en 0 hasta abrir el drawer una vez.
  // Round 2026-07-02.
  useEffect(() => {
    if (db?.user) {
      fetchAlertas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db?.user]);

  const handleSaveRecurrencia = async (next: Recurrencia | null) => {
    if (!recurrenciaTask) return;
    setRecurrenciaSaving(true);
    try {
      await store.upsertTask({
        ...(recurrenciaTask as any),
        recurrencia: next,
      });
      getMsg()?.success(
        next ? "Recurrencia configurada" : "Recurrencia eliminada",
      );
      // La recurrencia cambia las "recurrencias próximas" del drawer.
      void fetchAlertas();
      setRecurrenciaTask(null);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error guardando recurrencia");
    } finally {
      setRecurrenciaSaving(false);
    }
  };
  const [propDrawerOpen, setPropDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Preferencia de vista: la barra de Espacios colapsada a un riel de
  // iniciales. No recorta datos, pero es una elección que molesta rehacer.
  const [sidebarCollapsed, setSidebarCollapsed] = useFiltroPersistido<boolean>(
    "tareas.barraColapsada",
    false,
    { validar: (v) => typeof v === "boolean" },
  );
  const [newCategoryName, setNewCategoryName] = useState("");
  const [draft, setDraft] = useState(emptyDraft);
  // Ref espejo del `draft`. PERF (round 2026-06-15): las columnas están
  // memoizadas SIN `draft` en sus deps. Los SELECTS/fechas/checkbox del draft
  // leen su valor de la fila `r` (= draftRow = { ...draft }); los INPUTS DE
  // TEXTO (descripción, costo, etc.) son no controlados y viven en `editBufRef`
  // (no tocan el state al tipear). Los handlers de commit leen el draft vigente
  // de este ref y materializan el texto con `mergeDraftBuffers`.
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  // PERF (round 2026-06-15, 2ª pasada): contador de "generación" del draft.
  // Los inputs de texto de la fila nueva son NO controlados (defaultValue +
  // buffer en editBufRef), para que tipear NO llame a setDraft → NO re-renderee
  // el componente ni la tabla. Al crear/resetear el draft, incrementamos
  // `draftGen`; los inputs usan `draftGen` en su `key`, así se remontan y se
  // limpian. La causa real del lag era que `dataSource` cambiaba de referencia
  // en cada tecla (draftRow lleva el texto), y rc-table re-renderiza TODO el
  // cuerpo cuando cambia la referencia del dataSource.
  const [draftGen, setDraftGen] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [createName, setCreateName] = useState("");
  const [createTemplate, setCreateTemplate] = useState<
    "default" | "consultas" | "gerencia" | "custom"
  >("default");
  // El getter no se usa: el modo se aplica via el setter al confirmar.
  const [, setCreateMode] = useState<SpaceMode>("board");
  const [selectedCols, setSelectedCols] = useState<string[]>(
    columnsTareasDefault.map((c) => c.key),
  );
  // Sectores con los que se vincula el espacio al crearlo. Pueden ser CERO
  // (sin filtro: todas las propiedades visibles) o MUCHOS. Cuando hay al
  // menos uno, las propiedades elegibles para las tareas del espacio se
  // restringen a las pertenecientes a alguno de esos sectores. Para system
  // spaces no aplica (Consultas/Gerencia no se replican desde la UI).
  const [createSectorIds, setCreateSectorIds] = useState<string[]>([]);
  // Flag UI mientras corre el DELETE del espacio — bloquea clicks repetidos
  // y muestra cursor "wait" en el botón de papelera. La doble confirmación
  // se maneja con Modal.confirm encadenados, no con state intermedio.
  const [deletingSpace, setDeletingSpace] = useState(false);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<DraftRow | null>(null);
  // GalleryItem: la galeria distingue imagen vs video por `kind` (no por la
  // extension del URL — las URLs firmadas /files/<JWT> no llevan extension).
  // El backend devuelve `items` con kind/mimeType/originalName/url/thumbUrl.
  // `key` y `comentario` se agregaron para soportar edición de comentarios
  // por foto/video (admin escribe, cliente lee). Ver
  // backend/src/controllers/tasks.controller.js setAttachmentComment.
  const [images, setImages] = useState<
    Array<{
      url: string;
      key?: string;
      kind?: string;
      originalName?: string;
      thumbUrl?: string | null;
      /** Versión liviana (480p) del video: GET /tareas/:id/images la manda en `items`. */
      previewUrl?: string | null;
      comentario?: string | null;
      muted?: boolean;
    }>
  >([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  // El comentario por foto/video se edita INLINE dentro de la galería
  // (components/InlineComment.tsx), con estado local → sin lag y sin modal aparte.
  // Quitar audio de un video — admin/superadmin. Operación destructiva
  // (re-escribe el archivo en disco sin pista de audio). `mutingKey` es
  // el key del video que se está procesando (para mostrar loading + bloquear
  // el botón mientras corre ffmpeg en backend).
  const [mutingKey, setMutingKey] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [docsModalTaskId, setDocsModalTaskId] = useState<number | null>(null);
  const [docs, setDocs] = useState<DocumentMeta[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const editBufRef = useRef<Record<string, string>>({});
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const committingRef = useRef(false);
  const draftRowRef = useRef<HTMLTableRowElement | null>(null);
  const draftBlurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [fDesc, setFDesc] = useFiltroPersistido<string>("tareas.busqueda", "", {
    validar: (v) => typeof v === "string",
  });
  // PERF (round 2026-07-02): el input de búsqueda es controlado y sin
  // debounce; cada tecla recomputaba `filteredTasks` (incl. matching difuso) +
  // clonaba `dataSource` (nueva ref por fila) → rc-table re-renderizaba TODO el
  // cuerpo por pulsación (lag notorio en boards grandes). `useDeferredValue`
  // mantiene `fDesc` como única fuente de verdad (el input y el badge de
  // filtros siguen inmediatos; `clearAllFilters`/allowClear funcionan igual) y
  // difiere SOLO el filtrado/clonado/re-render caro a baja prioridad, así
  // tipear se siente fluido.
  const deferredFDesc = useDeferredValue(fDesc);
  // Los ids guardados se validan solo por forma: la lista de propiedades llega
  // del backend, así que una propiedad borrada se ve como un tag suelto en el
  // Select — el badge sobre "Limpiar" lo delata y un clic lo saca.
  const [fPropIds, setFPropIds] = useFiltroPersistido<(string | number)[]>(
    "tareas.propiedades",
    [],
    {
      validar: (v) =>
        Array.isArray(v) &&
        v.every((x) => typeof x === "string" || typeof x === "number"),
    },
  );
  // Filtros nuevos (round 2026-05-06):
  // - fEstado / fPrioridad: arrays de string. Vacío = sin filtro.
  // - fTrabajador: array de id de aseador (string).
  // - fVenceRange: rango de fechas para `terminadoEl` (deadline). null = sin filtro.
  // Cualquier filtro usado se contabiliza en `activeFilterCount` para mostrar
  // un badge sobre el botón "Limpiar".
  const [fEstado, setFEstado] = useFiltroPersistido<string[]>(
    "tareas.estado",
    [],
    {
      validar: (v) =>
        Array.isArray(v) &&
        v.every((x) => ESTADOS_SECTOR.some((e) => e.value === x)),
    },
  );
  const [fPrioridad, setFPrioridad] = useFiltroPersistido<string[]>(
    "tareas.prioridad",
    [],
    {
      validar: (v) =>
        Array.isArray(v) &&
        v.every((x) => PRIORIDADES_TAREA.some((p) => p.value === x)),
    },
  );
  const [fTrabajador, setFTrabajador] = useFiltroPersistido<string[]>(
    "tareas.trabajador",
    [],
    { validar: (v) => Array.isArray(v) && v.every((x) => typeof x === "string") },
  );
  // El rango guarda texto y caduca a los 7 días (useRangoPersistido): un rango
  // viejo dejaría la tabla vacía sin explicación, porque el filtro descarta
  // toda tarea sin fecha de término.
  const [fVenceRange, setFVenceRange] = useRangoPersistido(
    "tareas.vence",
    null,
  );

  // Selección masiva para borrado en lote — solo admin/superadmin.
  // Guardamos `React.Key[]` (Id es string|number; AntD usa Key). La fila
  // "draft" (creación inline) tiene id="draft" y queda excluida de la
  // selección via `rowSelection.getCheckboxProps`. Limpiamos selección al
  // cambiar de space/categoría para no arrastrar ids de un contexto a otro.
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);
  // Categoria destino del popover "Mover a...". `null` = no elegida.
  const [bulkMoveTarget, setBulkMoveTarget] = useState<string | null>(null);
  const [bulkMovePopoverOpen, setBulkMovePopoverOpen] = useState(false);

  const handleBulkMove = async () => {
    if (!selectedRowKeys.length || !bulkMoveTarget) return;
    setBulkMoving(true);
    try {
      const taskIdSet = new Set(tasks.map((t) => String(t.id)));
      const ids = selectedRowKeys
        .filter((k) => taskIdSet.has(String(k)))
        .map((k) => {
          const t = tasks.find((x) => String(x.id) === String(k));
          return t!.id;
        });
      if (!ids.length) {
        setSelectedRowKeys([]);
        setBulkMovePopoverOpen(false);
        return;
      }
      const targetName =
        (activeSpace?.categories ?? []).find(
          (c: any) => String(c.id) === String(bulkMoveTarget),
        )?.name ?? "categoria";
      const { ok, fail } = await store.bulkMoveTasks(ids, bulkMoveTarget);
      if (ok > 0 && fail === 0) {
        getMsg()?.success(
          `${ok} tarea${ok === 1 ? "" : "s"} movida${ok === 1 ? "" : "s"} a "${targetName}"`,
        );
      } else if (ok > 0 && fail > 0) {
        getMsg()?.warning(`${ok} movida${ok === 1 ? "" : "s"}, ${fail} con error`);
      } else {
        getMsg()?.error("No se pudo mover ninguna tarea");
      }
      setSelectedRowKeys([]);
      setBulkMoveTarget(null);
      setBulkMovePopoverOpen(false);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error al mover");
    } finally {
      setBulkMoving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    setBulkDeleting(true);
    try {
      // Filtramos por las id's que existen actualmente en `tasks` (descarta
      // posibles "draft" y filas que ya hubieran sido borradas por otro tab
      // via WS). Mantenemos el tipo original (string|number) — el endpoint
      // hace encodeURIComponent en `useTasksLogic`.
      const taskIdSet = new Set(tasks.map((t) => String(t.id)));
      const ids = selectedRowKeys
        .filter((k) => taskIdSet.has(String(k)))
        .map((k) => {
          // Preserva el tipo original (number si era number) buscando en tasks
          const t = tasks.find((x) => String(x.id) === String(k));
          return t!.id;
        });
      if (!ids.length) {
        setSelectedRowKeys([]);
        return;
      }
      const { ok, fail } = await store.bulkDeleteTasks(ids);
      if (ok > 0 && fail === 0) {
        getMsg()?.success(
          `${ok} tarea${ok === 1 ? "" : "s"} eliminada${ok === 1 ? "" : "s"}`,
        );
      } else if (ok > 0 && fail > 0) {
        getMsg()?.warning(
          `${ok} eliminada${ok === 1 ? "" : "s"}, ${fail} con error`,
        );
      } else {
        getMsg()?.error("No se pudo eliminar ninguna tarea");
      }
      setSelectedRowKeys([]);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error en borrado masivo");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Limpia la selección cuando el usuario cambia de space o categoría —
  // evita arrastrar ids de un contexto a otro.
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [activeSpace?.id, activeCategory?.id]);

  // Similaridad liviana para búsqueda "difusa" sin dependencias.
  const diceSimilarity = (a: string, b: string) => {
    const s1 = norm(a);
    const s2 = norm(b);
    if (!s1 || !s2) return 0;
    if (s1 === s2) return 1;
    if (s1.length < 2 || s2.length < 2) return s1 === s2 ? 1 : 0;

    const bigrams = (s: string) => {
      const out: string[] = [];
      for (let i = 0; i < s.length - 1; i++) out.push(s.slice(i, i + 2));
      return out;
    };

    const bg1 = bigrams(s1);
    const bg2 = bigrams(s2);
    const counts = new Map<string, number>();
    for (const x of bg1) counts.set(x, (counts.get(x) ?? 0) + 1);

    let inter = 0;
    for (const x of bg2) {
      const c = counts.get(x) ?? 0;
      if (c > 0) {
        inter++;
        counts.set(x, c - 1);
      }
    }
    return (2 * inter) / (bg1.length + bg2.length);
  };

  const descMatches = (desc: any, q: string) => {
    const query = norm(q);
    if (!query) return true;
    const text = norm(desc);
    if (!text) return false;

    // 1) match por tokens (rápido y predecible)
    const tokens = query.split(/\s+/).filter(Boolean);
    const tokenOk = tokens.every((t) => text.includes(t));
    if (tokenOk) return true;

    // 2) match difuso (solo si la query ya tiene algo de largo)
    if (query.length >= 4) {
      return diceSimilarity(text, query) >= 0.35; // ajustable
    }
    return false;
  };

  const rowId = (r: DraftRow) => String((r as any).id); // usa el id real SIEMPRE
  const bufKey = (r: DraftRow, field: keyof Task) =>
    `${rowId(r)}:${String(field)}`;

  // Único flujo de creación que exponemos hoy: espacios "custom" tipo board
  // con columnas estándar (mismo esquema que Papudo/Concón). Las opciones
  // "consultas"/"gerencia" eran para replicar los system spaces — algo que
  // no queremos (Consultas/Gerencia son únicos y se autocrean en backend).
  // Round 2026-05-05 simplificó a un solo template.
  const templateChanged = (t: typeof createTemplate) => {
    setCreateTemplate(t);
    setCreateMode("board");
    setSelectedCols(columnsTareasDefault.map((c) => c.key));
  };

  const createSpaceNow = async () => {
    // Validación cliente de nombre único — defensa antes de tocar el
    // backend; el backend igual valida y devuelve 409 si se cuela algo
    // (race conditions, casos legacy). Match case-insensitive contra los
    // spaces que vemos en `db.spaces`.
    const trimmed = createName.trim();
    if (!trimmed) {
      getMsg()?.error("El nombre no puede estar vacío");
      return;
    }
    const lower = trimmed.toLowerCase();
    const dup = (db?.spaces ?? []).some((s) => s.name.toLowerCase() === lower);
    if (dup) {
      getMsg()?.error(`Ya existe un espacio llamado "${trimmed}"`);
      return;
    }
    const nuevo = {
      id: `custom:${uid()}`,
      name: trimmed,
      mode: "board" as const,
      columns: ALL_COLUMNS,
      // sectorIds: cero o más. Convertimos a Number[] limpiando NaN.
      sectorIds: createSectorIds
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n)),
    };
    await store.createSpace(nuevo);
    setCreateSectorIds([]);
  };

  // Punto de entrada del flujo de borrado. Doble confirmación:
  // 1) "¿Eliminar el espacio X?" — el usuario confirma intención.
  // 2) "También se borrarán todas las tareas dentro" — el usuario asume las
  //    consecuencias de borrar tareas potencialmente con histórico.
  // No contamos tareas localmente porque `tasks` solo trae las del space
  // activo; el backend hace la cuenta y la devuelve en la respuesta para
  // que el toast final indique cuántas se borraron.
  const askDeleteSpace = (space: { id: string; name: string; isSystem?: boolean }) => {
    if (space.isSystem) return; // defensa: ya no renderizamos el botón en system
    const modal = getModal();
    if (!modal) return;

    const runDelete = async () => {
      setDeletingSpace(true);
      try {
        const deletedTasks = await store.deleteSpace(space.id);
        getMsg()?.success(
          `Espacio "${space.name}" eliminado` +
            (deletedTasks > 0
              ? ` (${deletedTasks} tarea${deletedTasks === 1 ? "" : "s"} borrada${deletedTasks === 1 ? "" : "s"})`
              : ""),
        );
      } catch (e: any) {
        getMsg()?.error(e?.message || "Error eliminando espacio");
      } finally {
        setDeletingSpace(false);
      }
    };

    modal.confirm({
      title: `¿Eliminar el espacio "${space.name}"?`,
      content:
        "El espacio se eliminará del menú permanentemente. Si tiene tareas dentro, también se borrarán.",
      okText: "Continuar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => {
        modal.confirm({
          title: "Confirmar eliminación de tareas",
          content: `Esta acción borrará el espacio "${space.name}" y todas las tareas que contenga. No se puede deshacer.`,
          okText: "Sí, eliminar todo",
          okType: "danger",
          cancelText: "Cancelar",
          onOk: runDelete,
        });
      },
    });
  };

  // Eliminación de categoría con confirmación. Backend setea
  // `category_key=NULL` en las tareas que la referenciaban (no las borra),
  // así que solo necesitamos UNA confirmación — las tareas no se pierden,
  // solo quedan "sin categoría". El conteo lo sacamos del state local de
  // `tasks` (filtrando por categoryId).
  const askDeleteCategory = (cat: { id: string; name: string }) => {
    if (!activeSpace?.id) return;
    const modal = getModal();
    if (!modal) return;
    const affected = (tasks ?? []).filter(
      (t: any) => String(t.categoryId ?? "") === String(cat.id),
    ).length;
    modal.confirm({
      title: `¿Eliminar la categoría "${cat.name}"?`,
      content:
        affected > 0
          ? `${affected} tarea${affected === 1 ? "" : "s"} dentro de esta categoría quedará${affected === 1 ? "" : "n"} sin categoría asignada (no se eliminan). Esta acción no se puede deshacer.`
          : "Esta acción no se puede deshacer.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await store.removeCategory(activeSpace.id, cat.id);
          getMsg()?.success(`Categoría "${cat.name}" eliminada`);
        } catch (e: any) {
          getMsg()?.error(e?.message || "Error eliminando categoría");
        }
      },
    });
  };

  // Confirmar el rename inline de una categoría. Devuelve sin tocar nada si
  // el nombre no cambió o quedó vacío. Toast de error si el backend rechaza.
  // `renamingCategoryId` indica que el PATCH está en vuelo → la UI muestra
  // un Spin sobre el item afectado hasta que el refreshDb traiga el dato
  // fresco.
  const commitCategoryRename = async (catId: string, originalName: string) => {
    if (!activeSpace?.id) return;
    const trimmed = editingCategoryName.trim();
    setEditingCategoryId(null);
    if (!trimmed || trimmed === originalName) return;
    setRenamingCategoryId(catId);
    try {
      await store.renameCategory(activeSpace.id, catId, trimmed);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error renombrando categoría");
    } finally {
      setRenamingCategoryId(null);
    }
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const propiedades = db?.propiedades ?? [];
  const aseadores = db?.trabajadores ?? [];
  const sectores = (db as any)?.sectores ?? [];

  // Opciones AntD-shape memoizadas. Antes se construian inline en JSX dentro
  // de los renders de columnas (que se llaman por fila), creando arrays
  // identicos N veces y forzando re-render del Select.
  // ─── Filtrado por sectores del space activo ───────────────────────────────
  // Si el espacio activo está vinculado a uno o más sectores, restringimos
  // la columna "Propiedad" a las propiedades pertenecientes a alguno de
  // esos sectores. Spaces sin sectores (legacy + system + nuevos sin
  // vincular) muestran todas las propiedades. Si el filtro deja una
  // propiedad FUERA pero la tarea ya la tenía guardada, el dropdown no la
  // mostrará — pero el chip sigue renderizando porque la pintamos por id
  // contra el set completo (`propiedades`).
  const activeSectorIds = useMemo(() => {
    const raw = (activeSpace as any)?.sectorIds;
    if (!Array.isArray(raw)) return [] as number[];
    return raw.map((x: any) => Number(x)).filter((n: number) => Number.isFinite(n));
  }, [activeSpace]);
  const propiedadesFiltradas = useMemo(() => {
    if (activeSectorIds.length === 0) return propiedades;
    const set = new Set(activeSectorIds);
    return (propiedades as any[]).filter(
      (p) => p.sectorId != null && set.has(Number(p.sectorId)),
    );
  }, [propiedades, activeSectorIds]);
  const propiedadOptions = useMemo(
    () =>
      (propiedadesFiltradas as any[]).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [propiedadesFiltradas],
  );
  const sectorOptions = useMemo(
    () =>
      (sectores as any[]).map((s) => ({
        value: String(s.id),
        label: s.nombre,
      })),
    [sectores],
  );
  const aseadorOptions = useMemo(
    () => aseadores.map((w: any) => ({ value: String(w.id), label: w.name })),
    [aseadores],
  );
  const allColumnsOptions = useMemo(
    () => ALL_COLUMNS.map((c) => ({ label: c.label, value: c.key })),
    [],
  );
  const usuario = db?.user;
  const tipo = Array.isArray(usuario?.tipo) ? (usuario?.tipo as string[]) : [];
  const isAdmin = isAdminRole(tipo);
  const isCliente = tipo.includes("cliente");

  // Backend ya filtra espacios por rol; aquí solo defensa en profundidad
  const espaciosVisibles = (db?.spaces ?? []).filter((s) => {
    if (!usuario) return false;
    if (isAdmin) return true;
    if (s.isSystem) return false;
    return true;
  });

  const draftRow: DraftRow | null = useMemo(() => {
    if (!isAdmin) return null;
    if (!activeSpace?.id) return null;

    const categoryId =
      activeSpace.mode === "board" ? (activeCategory?.id ?? null) : null;

    return {
      id: "draft",
      spaceId: activeSpace.id,
      categoryId,
      ...draft,
      __draft: true,
    };
  }, [isAdmin, activeSpace?.id, activeSpace?.mode, activeCategory?.id, draft]);

  // Los cinco filtros de tablero (propiedades, estado, prioridad, aseador y
  // vencimiento) solo se muestran cuando el espacio es board (ver el render
  // más abajo). Un espacio de lista guarda `estadoConsulta`, no `estado`, y
  // no tiene propiedades ni aseador: si siguieran aplicándose, la tabla
  // quedaría en cero sin ningún control visible que lo explique — y desde
  // que los filtros se recuerdan (round 2026-09-24) eso sobrevive a la
  // recarga. Lo guardado no se toca: al volver al tablero reaparece.
  const enBoard = activeSpace?.mode === "board";

  const fPropSet = useMemo(
    () => new Set((fPropIds ?? []).map((x) => String(x))),
    [fPropIds],
  );

  const fEstadoSet = useMemo(() => new Set(fEstado), [fEstado]);
  const fPrioridadSet = useMemo(() => new Set(fPrioridad), [fPrioridad]);
  const fTrabajadorSet = useMemo(
    () => new Set(fTrabajador.map((x) => String(x))),
    [fTrabajador],
  );

  // Día actual recalculado en cada render de filtro — barato. Se usa para
  // el toggle "vencidas" y para chequear el rango de fechas en YYYY-MM-DD.
  const todayStr = ahoraChile().format("YYYY-MM-DD");

  const filteredTasks = useMemo(() => {
    return (tasks ?? []).filter((t) => {
      // Filtro por propiedades (al menos 1 coincide)
      if (enBoard && fPropSet.size > 0) {
        const ids = Array.isArray((t as any).propiedadIds)
          ? (t as any).propiedadIds.map((x: any) => String(x))
          : [];
        if (!ids.some((id: string) => fPropSet.has(id))) return false;
      }

      // Filtro por descripción (sin tildes / mayúsculas + difuso liviano)
      if (!descMatches((t as any).descripcion, deferredFDesc)) return false;

      // Estado / prioridad / aseador (multi-select; vacío = sin filtro).
      if (
        enBoard &&
        fEstadoSet.size > 0 &&
        !fEstadoSet.has(String((t as any).estado ?? ""))
      )
        return false;
      if (
        enBoard &&
        fPrioridadSet.size > 0 &&
        !fPrioridadSet.has(String((t as any).prioridad ?? ""))
      )
        return false;
      if (
        enBoard &&
        fTrabajadorSet.size > 0 &&
        !fTrabajadorSet.has(String((t as any).trabajadorId ?? ""))
      )
        return false;

      // Rango de fechas sobre `terminadoEl` (deadline / vencimiento).
      // Comparación lexicográfica YYYY-MM-DD que es == orden cronológico.
      if (enBoard && fVenceRange) {
        const vence = (t as any).terminadoEl as string | null | undefined;
        const [from, to] = fVenceRange;
        if (!vence) return false; // sin deadline → fuera del rango
        if (from && vence < from.format("YYYY-MM-DD")) return false;
        if (to && vence > to.format("YYYY-MM-DD")) return false;
      }

      return true;
    });
  }, [
    tasks,
    enBoard,
    fPropSet,
    deferredFDesc,
    fEstadoSet,
    fPrioridadSet,
    fTrabajadorSet,
    fVenceRange,
    todayStr,
  ]);

  // Cuenta de filtros activos para el badge sobre "Limpiar". `fDesc` y los
  // selects multi se cuentan si tienen contenido; los de tablero, solo en un
  // espacio board, que es donde se ven y se aplican (ver `enBoard`).
  const activeFilterCount =
    (fDesc ? 1 : 0) +
    (enBoard && fPropIds.length ? 1 : 0) +
    (enBoard && fEstado.length ? 1 : 0) +
    (enBoard && fPrioridad.length ? 1 : 0) +
    (enBoard && fTrabajador.length ? 1 : 0) +
    (enBoard && fVenceRange && (fVenceRange[0] || fVenceRange[1]) ? 1 : 0);

  const clearAllFilters = () => {
    setFDesc("");
    setFPropIds([]);
    setFEstado([]);
    setFPrioridad([]);
    setFTrabajador([]);
    setFVenceRange(null);
  };

  // Fila draft al INICIO de la tabla (antes iba al final). En tablas largas
  // bajar hasta el final para crear una tarea era tedioso, así que el draft
  // queda fijo arriba como una zona de "[+] añadir" siempre visible. Aplica
  // a todas las tablas de /tareas (board y list mode) porque sólo hay un
  // dataSource. Round 2026-05-05.
  // Firma de los colores de propiedad: cambia SOLO cuando se edita algún
  // color (en "Administrar propiedades"). El chip de propiedad en la tabla
  // pinta su color desde `db.propiedades`, pero un cambio de color NO toca las
  // tareas — así que sin esta firma en las deps de `dataSource`, las filas
  // conservan su referencia y rc-table no las vuelve a renderizar (el color
  // quedaba "pegado" hasta recargar). Round 2026-06-15.
  const propColorSig = useMemo(
    () =>
      (propiedades as any[])
        .map((p) => `${p.id}:${p.color ?? ""}`)
        .join("|"),
    [propiedades],
  );

  const dataSource: DraftRow[] = useMemo(() => {
    // Clonamos cada tarea para darle una referencia nueva cuando `dataSource`
    // se recalcula. Esto fuerza a rc-table a re-renderizar las filas cuando
    // cambian los colores de propiedad (ver `propColorSig`). Ya NO causa lag al
    // tipear: los inputs de la fila nueva son no controlados, así que escribir
    // no dispara re-render ni recalcula este memo.
    const base = (filteredTasks ?? []).map((t) => ({ ...t })) as DraftRow[];
    return draftRow ? [draftRow, ...base] : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTasks, draftRow, propColorSig]);

  // const dataSource: DraftRow[] = useMemo(() => {
  //   const rows: DraftRow[] = [...visibleTasks];
  //   if (draftRow) rows.push(draftRow);
  //   return rows;
  // }, [visibleTasks, draftRow]);

  const upsert = async (row: DraftRow) => {
    if (!isAdmin) return;
    if (!activeSpace?.id) return;

    const isDraft = row.__draft === true || row.id === "draft";

    const categoryId =
      activeSpace.mode === "board" ? (activeCategory?.id ?? null) : null;

    const cleaned: Task = {
      ...row,
      id: isDraft ? uid() : row.id,
      spaceId: activeSpace.id,
      categoryId,
    };

    const hasAnyValue =
      (Array.isArray(cleaned.propiedadIds) &&
        cleaned.propiedadIds.length > 0) ||
      !!cleaned.trabajadorId ||
      !!cleaned.prioridad ||
      !!cleaned.estado ||
      !!cleaned.terminadoEl ||
      cleaned.costo != null ||
      // list fields:
      !!cleaned.telefono ||
      !!cleaned.nombre ||
      !!cleaned.estadoConsulta ||
      !!cleaned.fecha ||
      !!cleaned.ubicacion ||
      !!cleaned.descripcion ||
      !!cleaned.importancia;

    if (isDraft && !hasAnyValue) return;
    await store.upsertTask(cleaned);
  };

  const updateCell = async (row: DraftRow, patch: Partial<Task>) => {
    if (!isAdmin) return;
    const next: DraftRow = { ...row, ...patch };
    await upsert(next);
    // Si el cambio afecta el cómputo de alertas (estado / fecha de
    // finalización / recurrencia), refrescamos el badge + drawer para que no
    // queden desincronizados. Ej: al auto-finalizar una tarea (fijar
    // "Terminado" en hoy/pasado), debe salir de "vencidas" al instante, no
    // recién al reabrir el drawer. Round 2026-07-02.
    if ("estado" in patch || "terminadoEl" in patch || "recurrencia" in patch) {
      void fetchAlertas();
    }
  };

  const buildColumns = (
    space: typeof activeSpace | null,
  ): ColumnsType<DraftRow> => {
    const mode: SpaceMode = space?.mode ?? "board";
    const baseDefs: ColumnDef[] = space?.columns?.length
      ? space.columns
      : columnsTareasDefault;

    // Orden preferido de columnas (las que no estén acá van al final)
    const preferredOrder = [
      "descripcion",
      "verCliente",
      "propiedadIds",
      "imagenes",
      "trabajadorId",
      "prioridad",
      "estado",
      "registradoEl",
      "terminadoEl",
      "costo",
    ];
    const sorted = [...baseDefs].sort((a, b) => {
      const ia = preferredOrder.indexOf(a.key);
      const ib = preferredOrder.indexOf(b.key);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    const colDefs: ColumnDef[] = isAdmin
      ? sorted
      : sorted.filter(
          (c) => c.key !== "verCliente" && c.key !== "trabajadorId",
        );

    const cols: ColumnsType<DraftRow> = [];

    const safeCommitDraft = async (nextDraft?: typeof draft) => {
      // Lee el draft vigente del ref (las columnas no dependen de `draft`).
      await commitDraft(nextDraft ?? draftRef.current);
    };

    const textInputCell = (
      r: DraftRow,
      field: keyof Task,
      placeholder = "—",
      opts?: { asTextArea?: boolean; rows?: number; width?: number | string },
    ) => {
      const isDraft = !!r.__draft;
      const persisted = String(((r as any)[field] ?? "") as any);

      // ---- DRAFT: NO CONTROLADO (PERF) ----
      // Antes era controlado: cada tecla llamaba setDraft → re-render del
      // componente + de TODA la tabla (rc-table re-renderiza el cuerpo cuando
      // cambia la referencia del dataSource, y draftRow/dataSource cambiaban
      // por tecla). Ahora el input es no controlado: lo tecleado vive en
      // `editBufRef` y NO toca el state. El commit (Enter, o blur de la fila)
      // lee el buffer vía `mergeDraftBuffers`. El `key` con `draftGen` remonta
      // el input cuando el draft se resetea tras crear, limpiándolo.
      // No commitear en cada blur — el commit ocurre al cambiar un
      // select/checkbox/date, presionar Enter, o el onBlur de la fila (que ya
      // verifica que el foco salió de la fila y no hay popups abiertos).
      if (isDraft) {
        const k = bufKey(r, field); // p.ej. "draft:descripcion"
        // defaultValue inicial = valor del draft al (re)montar.
        const defVal = String(((r as any)[field] ?? "") as any);

        const onChange = (
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          editBufRef.current[k] = e.target.value;
        };

        const onKeyDown = async (
          e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            editBufRef.current[k] = (e.target as any).value;
            await safeCommitDraft();
          }
        };

        const commonProps = {
          defaultValue: defVal,
          onChange,
          onKeyDown,
          placeholder,
          disabled: !isAdmin,
          style: opts?.width ? ({ width: opts.width } as any) : undefined,
        };

        const inputKey = `draft-${String(field)}-${draftGen}`;

        return opts?.asTextArea ? (
          <Input.TextArea
            key={inputKey}
            {...commonProps}
            autoSize={{ minRows: opts.rows ?? 2, maxRows: 6 }}
          />
        ) : (
          <Input key={inputKey} {...commonProps} />
        );
      }

      // ---- NO-DRAFT: UNCONTROLLED (NO se "pisará" al escribir) ----
      const k = bufKey(r, field);
      const isEditing = editingCell === k;

      const onChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        editBufRef.current[k] = e.target.value;
      };

      const onBlur = async (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        const newValue = e.target.value;

        if (newValue !== persisted) {
          await updateCell(r, { [field]: newValue } as any);
        }

        delete editBufRef.current[k];
        setEditingCell(null);
      };

      // Click-to-edit: muestra el texto persistido; click abre input.
      // Para campos largos (asTextArea, ej. descripción) mostramos hasta 2
      // líneas con ellipsis vertical (`line-clamp`) en lugar de una sola
      // línea — cabe más texto visible y el usuario suele necesitar leer
      // varias palabras antes de decidir si edita. Para campos de una sola
      // línea (texto corto) mantenemos el ellipsis horizontal clásico.
      if (!isEditing) {
        const multiline = !!opts?.asTextArea;
        return (
          <div
            onClick={() => {
              if (isAdmin) setEditingCell(k);
            }}
            title={persisted || undefined}
            style={
              multiline
                ? {
                    cursor: isAdmin ? "pointer" : "default",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.35",
                    padding: "4px 0",
                    minHeight: 22,
                    color: persisted ? undefined : "#bbb",
                  }
                : {
                    cursor: isAdmin ? "pointer" : "default",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding: "4px 0",
                    minHeight: 22,
                    color: persisted ? undefined : "#bbb",
                  }
            }
          >
            {persisted || placeholder}
          </div>
        );
      }

      const commonProps = {
        defaultValue: persisted,
        onChange,
        onBlur,
        placeholder,
        disabled: !isAdmin,
        autoFocus: true,
        style: opts?.width ? ({ width: opts.width } as any) : undefined,
        onPressEnter: (ev: any) => (ev.currentTarget as HTMLElement)?.blur?.(),
      };

      return opts?.asTextArea ? (
        <Input.TextArea
          {...(commonProps as any)}
          autoSize={{ minRows: opts?.rows ?? 2, maxRows: 6 }}
        />
      ) : (
        <Input {...(commonProps as any)} />
      );
    };

    const dateCell = (r: DraftRow, field: keyof Task) => {
      const isDraft = !!r.__draft;
      // `r` ya trae el valor del draft (draftRow = { ...draft }).
      const value = (r as any)[field] as string | null | undefined;

      return (
        <DatePicker
          value={value ? dayjs(value, "YYYY-MM-DD") : null}
          onChange={async (d) => {
            const iso = d ? d.format("YYYY-MM-DD") : null;

            // "Terminado" = fecha de finalización (decisión del dueño,
            // 2026-07-02): si se fija en HOY o en el PASADO, la tarea pasa
            // automáticamente a estado "finalizado" y sale de las alertas de
            // vencidas. Una fecha FUTURA no auto-finaliza (funciona como
            // plazo: avisa cuando llega). Solo aplica a la columna terminadoEl,
            // no a registradoEl.
            const autoFinalizar =
              field === "terminadoEl" &&
              iso != null &&
              iso <= todayStr &&
              String((r as any).estado ?? "") !== "finalizado";

            const patch: Partial<Task> = autoFinalizar
              ? ({ [field]: iso, estado: "finalizado" } as any)
              : ({ [field]: iso } as any);

            if (isDraft) {
              setDraft((prev) => ({ ...prev, ...patch }) as any);
            } else {
              await updateCell(r, patch);
            }
          }}
          disabled={!isAdmin}
          style={{ width: "100%" }}
          format="DD/MM/YY"
        />
      );
    };

    // Abre el modal limpiando `images` y activando `imagesLoading` antes del
    // fetch para que el render muestre spinner en vez del placeholder vacío
    // "No hay imágenes" mientras el backend responde.
    const openImageModal = async (task: DraftRow) => {
      setCurrentTask(task);
      setImages([]);
      // El Carousel siempre arranca en el slide 0; reseteamos `currentSlide`
      // para que el comentario inline (que ahora se renderiza FUERA del
      // carrusel y se ata a `currentSlide`) muestre el del primer elemento y
      // no arrastre el índice de la tarea vista antes. Round 2026-07-02.
      setCurrentSlide(0);
      setImagesLoading(true);
      setIsImageModalVisible(true);
      try {
        const items = await store.getImages(task.id);
        setImages(items || []);
      } catch {
        getMsg()?.error("Error cargando imágenes");
        // fallback: si tu tarea trae imagenes embebidas (objetos con url+kind)
        const fallback = (task.imagenes ?? [])
          .filter((x: any) => x?.url)
          .map((x: any) => ({ url: x.url, kind: x.kind, originalName: x.name }));
        setImages(fallback);
      } finally {
        setImagesLoading(false);
      }
    };

    const imagesCell = (r: DraftRow) => {
      const isDraft = !!r.__draft;
      const count = Array.isArray((r as any).imagenes)
        ? (r as any).imagenes.length
        : 0;

      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            icon={<CameraOutlined />}
            size="small"
            onClick={() => !isDraft && openImageModal(r)}
            disabled={isDraft}
          />
          <span style={{ color: "rgba(0,0,0,0.55)", fontSize: 12 }}>
            {count ? `${count}` : "—"}
          </span>
          <Tooltip title="Documentos">
            <Button
              icon={<FileOutlined />}
              size="small"
              onClick={() => !isDraft && openDocsModal(r.id)}
              disabled={isDraft}
            />
          </Tooltip>
        </div>
      );
    };

    const verClienteCell = (r: DraftRow) => {
      const isDraft = !!r.__draft;
      const value = !!(r as any).verCliente;

      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            cursor: isAdmin ? "pointer" : "default",
          }}
          onClick={() => {
            if (!isAdmin) return;
            const next = !value;
            if (isDraft) {
              setDraft((d) => ({ ...d, verCliente: next }) as any);
            } else {
              updateCell(r, { verCliente: next } as any);
            }
          }}
        >
          {value ? (
            <CheckOutlined style={{ color: "#52c41a", fontSize: 18 }} />
          ) : (
            <span style={{ color: "#d9d9d9", fontSize: 14 }}>—</span>
          )}
        </div>
      );
    };

    const makeBoardColumn = (def: ColumnDef) => {
      switch (def.key) {
        case "descripcion":
          return {
            title: "Descripción",
            dataIndex: "descripcion",
            key: "descripcion",
            width: 240,
            // No usar `ellipsis: true` aquí porque AntD lo aplica como
            // truncado de UNA sola línea sobre toda la celda — anula el
            // line-clamp 2 que aplica `textInputCell` con `asTextArea`.
            // El cell render se encarga del overflow correcto (2 lineas).
            sorter: (a: any, b: any) =>
              (a.descripcion ?? "").localeCompare(b.descripcion ?? ""),
            render: (_: any, r: DraftRow) => {
              const cell = textInputCell(
                r,
                "descripcion",
                r.__draft
                  ? "+ Nueva tarea — escribe la descripción y presiona Enter"
                  : "Añadir descripción",
                { asTextArea: true, rows: 2 },
              );
              if (r.__draft) return cell;
              const rec = (r as any).recurrencia as Recurrencia | null;
              // Tag 🔁 cuando la tarea es plantilla recurrente. Click abre
              // el modal de configuración. Solo admin lo ve interactivo;
              // otros roles ven el icono pero el modal no se abre.
              return (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>{cell}</div>
                  {isAdmin && (
                    <Tooltip
                      title={
                        rec
                          ? `🔁 Cada ${rec.cada} ${rec.tipo}${rec.cada > 1 ? "s" : ""} — próxima: ${dayjs(rec.proximaEl).format("DD/MM/YY")}`
                          : "Configurar recurrencia"
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<SyncOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRecurrenciaTask(r as any);
                        }}
                        style={{
                          flexShrink: 0,
                          color: rec ? "var(--primary-color)" : "#bbb",
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              );
            },
          } as any;

        case "propiedadIds":
          return {
            title: "Propiedad",
            dataIndex: "propiedadIds",
            key: "propiedadIds",
            width: 200,
            sorter: (a: any, b: any) => {
              const nameA =
                propiedades.find(
                  (p) => String(p.id) === String((a.propiedadIds ?? [])[0]),
                )?.name ?? "";
              const nameB =
                propiedades.find(
                  (p) => String(p.id) === String((b.propiedadIds ?? [])[0]),
                )?.name ?? "";
              return nameA.localeCompare(nameB);
            },
            render: (_: any, r: DraftRow) => {
              const isDraft = !!r.__draft;
              const value = r.propiedadIds ?? [];

              return (
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Seleccionar"
                  value={value}
                  options={propiedadOptions}
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  disabled={!isAdmin}
                  onChange={(ids) => {
                    if (!isAdmin) return;
                    if (isDraft) setDraft((d) => ({ ...d, propiedadIds: ids }));
                    else updateCell(r, { propiedadIds: ids });
                  }}
                  tagRender={(props) => {
                    // String() en ambos lados: `propiedades[].id` viene del
                    // backend como string, pero `props.value` (el id guardado
                    // en la tarea) puede ser número en datos legacy. Sin
                    // coercionar, `"5" === 5` es false y el chip caía a gris
                    // (mismo patrón que el sorter de arriba). Round 2026-06-15.
                    const prop = propiedades.find(
                      (x) => String(x.id) === String(props.value),
                    );
                    const bg = prop?.color || "#9CA3AF";
                    const fullName = prop?.name ?? (props.label as any);
                    return (
                      // Tooltip con el nombre completo: el chip trunca con
                      // ellipsis cuando el nombre es largo (.chip-text), asi
                      // que al pasar el mouse mostramos el nombre entero.
                      <Tooltip title={fullName}>
                        <span
                          className="chip"
                          style={{
                            backgroundColor: bg,
                            opacity: isAdmin ? 1 : 0.6,
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <span className="chip-text">{fullName}</span>

                          {isAdmin && (
                            <span className="chip-x" onClick={props.onClose}>
                              ×
                            </span>
                          )}
                        </span>
                      </Tooltip>
                    );
                  }}
                />
              );
            },
          } as any;

        case "trabajadorId":
          return {
            title: "Aseador",
            dataIndex: "trabajadorId",
            key: "trabajadorId",
            width: 140,
            align: "center",
            sorter: (a: any, b: any) => {
              const nameA =
                aseadores.find((w) => String(w.id) === String(a.trabajadorId))
                  ?.name ?? "";
              const nameB =
                aseadores.find((w) => String(w.id) === String(b.trabajadorId))
                  ?.name ?? "";
              return nameA.localeCompare(nameB);
            },
            render: (_: any, r: DraftRow) => {
              const isDraft = !!r.__draft;
              const value = r.trabajadorId;

              return (
                <Select
                  showSearch
                  allowClear
                  placeholder="Seleccionar"
                  options={aseadorOptions}
                  value={value != null ? String(value) : undefined}
                  onChange={(v) => {
                    const id = v != null ? Number(v) : null;

                    if (isDraft) {
                      setDraft((d) => ({ ...d, trabajadorId: id }));
                    } else {
                      updateCell(r, { trabajadorId: id });
                    }
                  }}
                  disabled={!isAdmin}
                  style={{ width: "100%" }}
                  optionFilterProp="label"
                />
              );
            },
          } as any;

        // Pri (prioridad). Mostramos solo la primera letra de la prioridad
        // dentro de una pill de color (A/M/B/P) cuando el Select está
        // cerrado, y la pill completa con el label entero dentro del
        // dropdown.
        //
        // IMPORTANTE: usamos `<Select.Option>` nativos + `optionLabelProp`
        // en lugar de `dropdownRender` con `<div onClick>` manuales. La
        // versión anterior con `dropdownRender` rompía la creación inline:
        // al ser la fila draft el único Select sin Option-hijo (porque
        // `selected` era undefined cuando aún no había prioridad), el
        // click sobre los `<div>` del popup portaleado a `body` no se
        // entregaba al draft sino al Select de la fila siguiente que sí
        // tenía Option (React 19 + AntD v5 + portal). Las options nativas
        // solucionan el bug porque AntD captura el click dentro del propio
        // popup y dispara `onChange` en el Select correcto.
        case "prioridad":
          return {
            title: "Pri",
            key: "prioridad",
            align: "center",
            showSorterTooltip: {
              title: "Prioridad: indica qué tan importante es la tarea",
            },
            width: 60,
            sorter: (a: any, b: any) =>
              (a.prioridad ?? "").localeCompare(b.prioridad ?? ""),
            render: (_: any, r: DraftRow) => {
              const isDraft = !!r.__draft;
              const value = r.prioridad;

              return (
                <Select
                  className="priority-select"
                  placeholder="—"
                  value={value ?? undefined}
                  disabled={!isAdmin}
                  // Round 2026-05-08 (iter 7): allowClear + pseudo-opción
                  // "Quitar" (al final del dropdown). Damos las dos vías
                  // porque la columna es estrecha (width 60) y la pill
                  // de color ocupa todo el ancho — el botón X de
                  // allowClear puede quedar tapado visualmente, así que
                  // la opción del dropdown es el método principal
                  // descubrible.
                  allowClear
                  suffixIcon={null}
                  dropdownMatchSelectWidth={false}
                  // Cuando el Select está cerrado se muestra el JSX del
                  // prop `label` de la Option seleccionada (pill abreviada);
                  // cuando está abierto, los `children` de cada Option (pill
                  // completa).
                  optionLabelProp="label"
                  style={{
                    width: "100%",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  onChange={(v) => {
                    // value="__clear__" es la pseudo-opción "Quitar" del
                    // dropdown (definida más abajo). undefined ocurre
                    // cuando el usuario clic en el X de allowClear.
                    // Ambos casos → null.
                    const nextVal = v == null || v === "__clear__" ? null : v;

                    if (isDraft) {
                      setDraft((d) => ({ ...d, prioridad: nextVal }));
                    } else {
                      updateCell(r, { prioridad: nextVal });
                    }
                  }}
                >
                  {PRIORIDADES_TAREA.map((p) => (
                    <Select.Option
                      key={p.value}
                      value={p.value}
                      label={
                        <Tooltip title={p.label}>
                          <div
                            style={{
                              backgroundColor: p.color,
                              color: "white",
                              padding: "2px 0",
                              borderRadius: 6,
                              textAlign: "center",
                              fontWeight: "bold",
                              width: "100%",
                              fontSize: 13,
                            }}
                          >
                            {p.label[0]}
                          </div>
                        </Tooltip>
                      }
                    >
                      <div
                        style={{
                          backgroundColor: p.color,
                          padding: "6px 10px",
                          borderRadius: 6,
                          color: "white",
                          textAlign: "center",
                          fontWeight: "bold",
                          margin: "2px 0",
                        }}
                      >
                        {p.label}
                      </div>
                    </Select.Option>
                  ))}
                  {/*
                    Pseudo-opción "Quitar" (round 2026-05-08 iter 7).
                    Solo aparece cuando ya hay un valor seleccionado — si
                    el campo está vacío, ofrecer "quitar" sería ruido.
                    Render visual: pill con borde punteado gris, sin
                    color de fondo, para distinguirla claramente de las
                    opciones reales con color. Al cliquearla, onChange
                    recibe "__clear__" y lo convertimos a null.
                  */}
                  {value != null && (
                    <Select.Option
                      key="__clear__"
                      value="__clear__"
                      label={null}
                    >
                      <div
                        style={{
                          padding: "6px 10px",
                          borderRadius: 6,
                          textAlign: "center",
                          fontWeight: 600,
                          fontSize: 12,
                          color: "#6b7280",
                          border: "1px dashed #d1d5db",
                          backgroundColor: "#fafafa",
                          margin: "2px 0",
                        }}
                      >
                        — Quitar
                      </div>
                    </Select.Option>
                  )}
                </Select>
              );
            },
          } as any;

        // Est (estado). Mismo patrón que prioridad: pill abreviada (R/E/F/P)
        // cuando el Select está cerrado, y pill completa con el label en el
        // dropdown. Ver el comentario del case "prioridad" para el detalle
        // de por qué usamos options nativas en vez de `dropdownRender`.
        case "estado":
          return {
            title: "Est",
            dataIndex: "estado",
            key: "estado",
            showSorterTooltip: {
              title: "Estado: indica el estado actual de la tarea",
            },
            width: 60,
            sorter: (a: any, b: any) =>
              (a.estado ?? "").localeCompare(b.estado ?? ""),
            align: "center",
            render: (_: any, r: DraftRow) => {
              const isDraft = !!r.__draft;
              const value = r.estado;

              return (
                <Select
                  className="priority-select"
                  placeholder="—"
                  value={value ?? undefined}
                  disabled={!isAdmin}
                  // Round 2026-05-08 (iter 7): ver el comentario
                  // homólogo en `case "prioridad"` para el motivo
                  // de tener allowClear + pseudo-opción "Quitar".
                  allowClear
                  suffixIcon={null}
                  dropdownMatchSelectWidth={false}
                  optionLabelProp="label"
                  style={{
                    width: "100%",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  onChange={(v) => {
                    // La pseudo-opción "__clear__" del final del
                    // dropdown se mapea a null para permitir desasignar
                    // el estado. allowClear también dispara onChange
                    // con undefined.
                    const nextVal = v == null || v === "__clear__" ? null : v;

                    if (isDraft) {
                      setDraft((d) => ({ ...d, estado: nextVal }));
                    } else {
                      updateCell(r, { estado: nextVal });
                    }
                  }}
                >
                  {ESTADOS_SECTOR.map((e) => (
                    <Select.Option
                      key={e.value}
                      value={e.value}
                      label={
                        <Tooltip title={e.label}>
                          <div
                            style={{
                              backgroundColor: e.color,
                              color: "white",
                              padding: "2px 0",
                              borderRadius: 6,
                              textAlign: "center",
                              fontWeight: "bold",
                              fontSize: 13,
                            }}
                          >
                            {e.label[0]}
                          </div>
                        </Tooltip>
                      }
                    >
                      <div
                        style={{
                          backgroundColor: e.color,
                          padding: "6px 10px",
                          borderRadius: 6,
                          color: "white",
                          textAlign: "center",
                          fontWeight: "bold",
                          margin: "2px 0",
                        }}
                      >
                        {e.label}
                      </div>
                    </Select.Option>
                  ))}
                  {/* Pseudo-opción "Quitar" — round 2026-05-08 iter 7.
                      Solo visible cuando hay valor seleccionado. */}
                  {value != null && (
                    <Select.Option
                      key="__clear__"
                      value="__clear__"
                      label={null}
                    >
                      <div
                        style={{
                          padding: "6px 10px",
                          borderRadius: 6,
                          textAlign: "center",
                          fontWeight: 600,
                          fontSize: 12,
                          color: "#6b7280",
                          border: "1px dashed #d1d5db",
                          backgroundColor: "#fafafa",
                          margin: "2px 0",
                        }}
                      >
                        — Quitar
                      </div>
                    </Select.Option>
                  )}
                </Select>
              );
            },
          } as any;

        case "registradoEl":
          return {
            title: "Registrado",
            dataIndex: "registradoEl",
            key: "registradoEl",
            width: 130,
            align: "center",
            sorter: (a: any, b: any) =>
              (a.registradoEl ?? "").localeCompare(b.registradoEl ?? ""),
            render: (_: any, r: DraftRow) => dateCell(r, "registradoEl"),
          } as any;

        case "terminadoEl":
          return {
            title: "Terminado",
            key: "terminadoEl",
            width: 130,
            align: "center",
            sorter: (a: any, b: any) =>
              (a.terminadoEl ?? "").localeCompare(b.terminadoEl ?? ""),
            render: (_: any, r: DraftRow) => dateCell(r, "terminadoEl"),
          } as any;

        case "costo":
          return {
            title: "Costo",
            dataIndex: "costo",
            key: "costo",
            width: 100,
            sorter: (a: any, b: any) => (a.costo ?? 0) - (b.costo ?? 0),
            render: (_: any, r: DraftRow) => {
              const isDraft = !!r.__draft;
              const rawValue = r.costo;
              const value =
                rawValue != null ? String(Math.floor(rawValue)) : "";

              return (
                <Input
                  type="number"
                  prefix="$"
                  placeholder="0"
                  defaultValue={value}
                  key={
                    isDraft
                      ? `costo-draft-${draftGen}`
                      : `costo-${r.id}-${rawValue}`
                  }
                  onChange={(e) => {
                    if (isDraft) {
                      // PERF: no controlado. Guardamos en buffer; se materializa
                      // al commitear (mergeDraftBuffers convierte a número).
                      editBufRef.current[bufKey(r, "costo")] = e.target.value;
                    }
                  }}
                  onBlur={async (e) => {
                    if (isDraft) return;
                    const v = e.target.value;
                    const num = v === "" ? null : Number(v);
                    if (Number.isNaN(num)) return;
                    await updateCell(r, { costo: num });
                  }}
                  disabled={!isAdmin}
                />
              );
            },
          } as any;

        case "imagenes":
          return {
            title: (
              <Tooltip title="Adjuntos: imágenes y documentos asociados a la tarea">
                Adjuntos
              </Tooltip>
            ),
            key: "imagenes",
            width: 80,
            render: (_: any, r: DraftRow) => imagesCell(r),
          } as any;

        case "verCliente":
          return {
            title: "Visible",
            key: "verCliente",
            width: 70,
            showSorterTooltip: {
              title:
                "Visible: indica si el cliente puede ver esta tarea. Si está marcado, el cliente tiene acceso",
            },
            sorter: (a: any, b: any) =>
              Number(!!a.verCliente) - Number(!!b.verCliente),
            render: (_: any, r: DraftRow) => verClienteCell(r),
          } as any;

        default:
          return null;
      }
    };

    const makeListColumn = (def: ColumnDef) => {
      // Para Consultas / Gerencia: usamos keys que viven en columnsConsultas / columnsGerencia
      // (telefono, nombre, estado, fecha, ubicacion, descripcion, importancia)
      switch (def.key) {
        case "telefono":
          return {
            title: "Teléfono",
            key: "telefono",
            width: 140,
            sorter: (a: any, b: any) =>
              ((a as any).telefono ?? "").localeCompare(
                (b as any).telefono ?? "",
              ),
            render: (_: any, r: DraftRow) =>
              textInputCell(r, "telefono" as any, "—"),
          } as any;

        case "nombre":
          return {
            title: "Nombre",
            key: "nombre",
            width: 160,
            sorter: (a: any, b: any) =>
              ((a as any).nombre ?? "").localeCompare((b as any).nombre ?? ""),
            render: (_: any, r: DraftRow) =>
              textInputCell(r, "nombre" as any, "—"),
          } as any;

        case "estadoConsulta":
          return {
            title: "Estado",
            key: "estadoConsulta",
            width: 160,
            sorter: (a: any, b: any) =>
              ((a as any).estadoConsulta ?? "").localeCompare(
                (b as any).estadoConsulta ?? "",
              ),
            render: (_: any, r: DraftRow) => {
              const isDraft = !!r.__draft;
              const value = (r as any).estadoConsulta;

              const selected = ESTADOS_CONSULTA.find((e) => e.value === value);

              return (
                <Select
                  allowClear
                  placeholder="—"
                  value={value ?? undefined}
                  onChange={async (v) => {
                    const nextVal = v ?? null;
                    if (isDraft) {
                      setDraft((d) => ({ ...d, estadoConsulta: nextVal }));
                    } else {
                      await updateCell(r, {
                        estadoConsulta: nextVal,
                      } as any);
                    }
                  }}
                  disabled={!isAdmin}
                  dropdownRender={(_menu) => (
                    <div style={{ padding: 8 }}>
                      {ESTADOS_CONSULTA.map((e) => (
                        <div
                          key={e.value}
                          style={{
                            backgroundColor: e.color,
                            padding: "6px 10px",
                            borderRadius: 6,
                            color: "white",
                            marginBottom: 4,
                            cursor: "pointer",
                            textAlign: "center",
                          }}
                          onClick={() => {
                            if (isDraft) {
                              setDraft((d) => ({
                                ...d,
                                estadoConsulta: e.value,
                              }));
                            } else {
                              updateCell(r, { estadoConsulta: e.value } as any);
                            }
                          }}
                        >
                          {e.label}
                        </div>
                      ))}
                    </div>
                  )}
                  // Este renderiza el valor seleccionado como un bloque de color
                  dropdownMatchSelectWidth={false}
                  style={{
                    width: "100%",
                    backgroundColor: "transparent", // Elimina fondo blanco
                    boxShadow: "none", // Quita borde en focus
                  }}
                  suffixIcon={null}
                  className="estado-select-custom"
                >
                  {selected && (
                    <Select.Option key={selected.value} value={selected.value}>
                      <div
                        style={{
                          backgroundColor: selected.color,
                          color: "white",
                          padding: "4px 10px",
                          borderRadius: 6,
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {selected.label}
                      </div>
                    </Select.Option>
                  )}
                </Select>
              );
            },
          } as any;

        case "fecha":
          return {
            title: "Fecha",
            key: "fecha",
            width: 130,
            sorter: (a: any, b: any) =>
              ((a as any).fecha ?? "").localeCompare((b as any).fecha ?? ""),
            render: (_: any, r: DraftRow) => dateCell(r, "fecha" as any),
          } as any;

        case "ubicacion":
          return {
            title: "Ubicación",
            key: "ubicacion",
            width: 180,
            sorter: (a: any, b: any) =>
              ((a as any).ubicacion ?? "").localeCompare(
                (b as any).ubicacion ?? "",
              ),
            render: (_: any, r: DraftRow) =>
              textInputCell(r, "ubicacion" as any, "—"),
          } as any;

        case "descripcion":
          return {
            title: "Descripción",
            key: "descripcion",
            width: 240,
            // Mismo motivo que en board mode: el cell render aplica
            // line-clamp 2; `ellipsis: true` de AntD lo anularia.
            sorter: (a: any, b: any) =>
              ((a as any).descripcion ?? "").localeCompare(
                (b as any).descripcion ?? "",
              ),
            render: (_: any, r: DraftRow) => {
              const cell = textInputCell(r, "descripcion" as any, "—", {
                asTextArea: true,
                rows: 2,
              });
              if (r.__draft) return cell;
              // Mismo ícono 🔁 que en board mode (Papudo/Concón). Aplica
              // a Consultas y Gerencia tambien — admin puede convertir
              // cualquier tarea en plantilla recurrente.
              const rec = (r as any).recurrencia as Recurrencia | null;
              return (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>{cell}</div>
                  {isAdmin && (
                    <Tooltip
                      title={
                        rec
                          ? `🔁 Cada ${rec.cada} ${rec.tipo}${rec.cada > 1 ? "s" : ""} — próxima: ${dayjs(rec.proximaEl).format("DD/MM/YY")}`
                          : "Configurar recurrencia"
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<SyncOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRecurrenciaTask(r as any);
                        }}
                        style={{
                          flexShrink: 0,
                          color: rec ? "var(--primary-color)" : "#bbb",
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              );
            },
          } as any;

        case "importancia":
          return {
            title: "Importancia",
            key: "importancia",
            width: 160,
            align: "center",
            sorter: (a: any, b: any) =>
              ((a as any).importancia ?? "").localeCompare(
                (b as any).importancia ?? "",
              ),
            render: (_: any, r: DraftRow) => {
              // Mismo patrón visual que prioridad/estado en board mode: pill
              // coloreada como valor seleccionado y como cada opción del
              // dropdown. Paleta semántica: Baja=verde, Media=amarillo,
              // Alta=naranja, Crítica=rojo. Reutilizamos `optionLabelProp`
              // para que la pill se muestre tal cual cuando el Select está
              // cerrado, igual que en `case "prioridad"`.
              const IMPORTANCIA_OPTS = [
                { value: "Baja", label: "Baja", color: "#22c55e" },
                { value: "Media", label: "Media", color: "#facc15" },
                { value: "Alta", label: "Alta", color: "#f97316" },
                { value: "Crítica", label: "Crítica", color: "#b91c1c" },
              ];

              const isDraft = !!r.__draft;
              const value = (r as any).importancia ?? null;

              return (
                <Select
                  className="priority-select"
                  allowClear
                  placeholder="—"
                  value={value ?? undefined}
                  disabled={!isAdmin}
                  suffixIcon={null}
                  dropdownMatchSelectWidth={false}
                  optionLabelProp="label"
                  style={{
                    width: "100%",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  onChange={async (v) => {
                    const nextVal = (v ?? null) as any;
                    if (isDraft) {
                      setDraft((d) => ({ ...d, importancia: nextVal }));
                    } else {
                      await updateCell(r, { importancia: nextVal } as any);
                    }
                  }}
                >
                  {IMPORTANCIA_OPTS.map((o) => (
                    <Select.Option
                      key={o.value}
                      value={o.value}
                      label={
                        <div
                          style={{
                            backgroundColor: o.color,
                            color: "white",
                            padding: "2px 10px",
                            borderRadius: 6,
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: 13,
                          }}
                        >
                          {o.label}
                        </div>
                      }
                    >
                      <div
                        style={{
                          backgroundColor: o.color,
                          padding: "6px 10px",
                          borderRadius: 6,
                          color: "white",
                          textAlign: "center",
                          fontWeight: "bold",
                          margin: "2px 0",
                        }}
                      >
                        {o.label}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              );
            },
          } as any;

        case "imagenes":
          return {
            title: (
              <Tooltip title="Adjuntos: imágenes y documentos asociados a la tarea">
                Adjuntos
              </Tooltip>
            ),
            key: "imagenes",
            width: 80,
            render: (_: any, r: DraftRow) => imagesCell(r),
          } as any;

        case "verCliente":
          return {
            title: "Visible",
            key: "verCliente",
            width: 70,
            showSorterTooltip: {
              title:
                "Visible: indica si el cliente puede ver esta tarea. Si está marcado, el cliente tiene acceso",
            },
            sorter: (a: any, b: any) =>
              Number(!!a.verCliente) - Number(!!b.verCliente),
            render: (_: any, r: DraftRow) => verClienteCell(r),
          } as any;

        default:
          return null;
      }
    };

    for (const def of colDefs) {
      const c = mode === "board" ? makeBoardColumn(def) : makeListColumn(def);
      if (c) cols.push(c);
    }

    if (isAdmin) {
      cols.push({
        title: "",
        key: "actions",
        width: 110,
        fixed: "right",
        render: (_: any, r: DraftRow) => {
          if (r.__draft) {
            // Botón explícito "Crear" además de Enter/blur. Da una acción
            // descubrible (antes solo había un "+" pasivo y el usuario no sabía
            // cómo confirmar). onMouseDown preventDefault evita que el click
            // robe el foco del textarea y dispare el commit por blur en
            // paralelo. `safeCommitDraft` lee el draft vigente del ref.
            return (
              <div className="draft-hint">
                <Tooltip title="Crear la tarea (o presiona Enter en la descripción)">
                  <Button
                    type="primary"
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => safeCommitDraft()}
                  >
                    Crear
                  </Button>
                </Tooltip>
              </div>
            );
          }
          // Fila optimista en vuelo: spinner + "Guardando…" mientras el backend
          // confirma la creación. Sin botón de eliminar (la tarea aún no existe
          // en el servidor; su id es temporal `tmp_*`).
          if ((r as any)._saving) {
            return (
              <div
                className="row-actions"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "rgba(0,0,0,0.45)",
                  fontSize: 12,
                }}
              >
                <Spin size="small" />
                <span>Guardando…</span>
              </div>
            );
          }
          return (
            <div className="row-actions">
              <Popconfirm
                title="¿Eliminar tarea?"
                okText="Sí"
                cancelText="No"
                onConfirm={() => store.deleteTask(r.id)}
              >
                <Button size="small" danger>
                  Eliminar
                </Button>
              </Popconfirm>
            </div>
          );
        },
      });
    }

    return cols;
  };

  // PERF (round 2026-06-15): `draft` ya NO está en las dependencias. Antes,
  // cada tecla recomputaba TODAS las columnas Y, peor, cambiaba la referencia
  // del `dataSource` (draftRow lleva el texto) → rc-table re-renderiza todo su
  // cuerpo (todas las filas con sus Select/DatePicker pesados) → lag por
  // pulsación. Ahora los inputs de texto del draft son no controlados (no
  // llaman setDraft al tipear), así que el componente y la tabla NO se
  // re-renderizan mientras se escribe.
  const columns = useMemo(
    () => buildColumns(activeSpace ?? null),
    // `draftGen` SÍ está en deps: los inputs no controlados del draft usan
    // `draftGen` en su `key` para remontarse (limpiarse) al crear. Cambia una
    // vez por creación, no por tecla, así que no reintroduce el lag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeSpace, db, editingItemId, editingCell, draftGen],
  );

  // Limpia los buffers de los inputs no controlados del draft (claves que
  // empiezan con "draft:") y remonta los inputs vía draftGen. PERF: ver el
  // comentario en la declaración de `draftGen`.
  const clearDraftBuffers = () => {
    for (const key of Object.keys(editBufRef.current)) {
      if (key.startsWith("draft:")) delete editBufRef.current[key];
    }
  };

  // Fusiona en el draft los valores tecleados en los inputs no controlados
  // (descripción, costo, teléfono, nombre, ubicación…). Estos NO viven en el
  // state mientras se escribe; solo se materializan aquí, al commitear. `costo`
  // se convierte de string a número (o null si quedó vacío).
  const mergeDraftBuffers = (base: typeof emptyDraft) => {
    const merged: any = { ...base };
    const prefix = "draft:";
    for (const key of Object.keys(editBufRef.current)) {
      if (!key.startsWith(prefix)) continue;
      const field = key.slice(prefix.length);
      const raw = editBufRef.current[key];
      if (field === "costo") {
        const num = raw === "" || raw == null ? null : Number(raw);
        merged.costo = Number.isNaN(num) ? merged.costo : num;
      } else {
        merged[field] = raw;
      }
    }
    return merged as typeof emptyDraft;
  };

  // Resetea el draft tras crear (o al cambiar de categoría): limpia buffers,
  // remonta los inputs (draftGen++) y vuelve el state a vacío con la fecha de
  // hoy.
  const resetDraftState = () => {
    clearDraftBuffers();
    setDraftGen((g) => g + 1);
    const today = ahoraChile().format("YYYY-MM-DD");
    setDraft({ ...emptyDraft, registradoEl: today, fecha: today });
  };

  // Al cambiar de categoría reseteamos el draft refrescando registradoEl/fecha
  // a `ahoraChile()` actual — `emptyDraft` está memoizado con deps vacías y
  // cachearía la fecha del primer mount. Cubre el caso del usuario con la
  // pestaña abierta cruzando medianoche. También limpia buffers + remonta
  // inputs no controlados (draftGen) para no arrastrar texto entre categorías.
  useEffect(() => {
    resetDraftState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory?.id, emptyDraft]);

  const hasAnyDraftValue = (d: typeof emptyDraft) => {
    return (
      d.descripcion?.trim() ||
      (Array.isArray(d.propiedadIds) && d.propiedadIds.length > 0) ||
      d.trabajadorId ||
      d.prioridad ||
      d.estado ||
      d.terminadoEl ||
      d.costo != null ||
      d.telefono ||
      d.nombre ||
      d.estadoConsulta ||
      d.fecha ||
      d.ubicacion ||
      d.descripcion ||
      d.importancia ||
      d.verCliente
    );
  };

  const commitDraft = async (dBase = draft) => {
    if (!activeSpace?.id) return;
    if (committingRef.current) return;

    const categoryId =
      activeSpace.mode === "board" ? (activeCategory?.id ?? null) : null;

    // Materializa lo tecleado en los inputs no controlados (buffer) sobre el
    // draft del state, que solo tiene los selects/fechas/checkbox.
    const d = mergeDraftBuffers(dBase);

    if (!hasAnyDraftValue(d)) return;

    // Requerimos descripción no vacía para auto-commitear. Antes el
    // commit se gatillaba con CUALQUIER campo lleno (propiedad, prioridad,
    // fecha...), por lo que al rellenar varios campos en orden el blur
    // intermedio creaba una tarea parcial SIN descripción y reseteaba el
    // draft, tirando los datos a "la línea siguiente". Si el usuario
    // realmente quiere crear sin descripción, puede igual hacerlo via
    // Enter en el campo descripción (que ya pasa por safeCommitDraft con
    // el value del input), pero ese flujo explícito casi nunca se da.
    const desc = (d.descripcion ?? "").trim();
    if (!desc) return;

    committingRef.current = true;
    const newTask: NewTask = {
      spaceId: activeSpace.id,
      categoryId,
      registradoPorId: user?.id,
      ...d,
    };
    // Limpia el draft YA (input + buffers + remonta inputs): feedback inmediato.
    // `newTask` ya capturó los valores fusionados, así que limpiar los buffers
    // ahora es seguro. Como la creación es optimista (la fila aparece al
    // instante con spinner "Guardando…"), no hay hueco visual entre vaciar el
    // input y ver la nueva fila.
    resetDraftState();
    try {
      await store.upsertTask(newTask);
    } finally {
      committingRef.current = false;
    }
  };

  // ── Documents ──────────────────────────────────────────────────────────────
  const openDocsModal = async (taskId: string) => {
    setDocsModalTaskId(Number(taskId));
    setDocsLoading(true);
    try {
      const result = await store.getDocuments(taskId);
      setDocs(result || []);
    } catch {
      getMsg()?.error("Error cargando documentos");
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const closeDocsModal = () => {
    setDocsModalTaskId(null);
    setDocs([]);
  };

  const handleUploadDocument = async (file: File) => {
    if (!docsModalTaskId) return;
    try {
      setDocsLoading(true);
      const uploaded = await store.uploadDocument(
        String(docsModalTaskId),
        file,
      );
      setDocs((prev) => [...prev, uploaded]);
      getMsg()?.success("Documento subido");
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error subiendo documento");
    } finally {
      setDocsLoading(false);
    }
  };

  const handleDeleteDocument = async (key: string) => {
    if (!docsModalTaskId) return;
    try {
      setDocsLoading(true);
      const remaining = await store.deleteDocument(
        String(docsModalTaskId),
        key,
      );
      setDocs(remaining);
      getMsg()?.success("Documento eliminado");
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error eliminando documento");
    } finally {
      setDocsLoading(false);
    }
  };

  // Sube un File (imagen o video) al backend. Si es imagen, primero la
  // comprime con `compressImageFile` aplicando los parámetros de la config
  // admin (`/administradores → Optimización de imágenes`). Videos van tal
  // cual. Aplica el límite cliente (25 MB) sobre el archivo ORIGINAL.
  const uploadDirect = async (file: File) => {
    if (!currentTask || saving) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      getMsg()?.error(
        `Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo permitido: 25 MB.`,
      );
      return;
    }
    try {
      setSaving(true);
      const toUpload = await compressImageFile(file);
      const newItem = await store.uploadMediaFile(currentTask.id, toUpload);
      setImages((prev) => [...prev, newItem]);
      getMsg()?.success(file.type.startsWith("video/") ? "Video agregado" : "Imagen agregada");
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error subiendo archivo");
    } finally {
      setSaving(false);
    }
  };

  // Ctrl+V paste support for images — active when the image modal is open
  useEffect(() => {
    if (!isImageModalVisible) return;
    const handler = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) =>
        f.type.startsWith("image/"),
      );
      if (!file) return;
      e.preventDefault();
      uploadDirect(file);
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageModalVisible, currentTask, saving]);

  // Inferir un nombre amigable para la descarga a partir de la posición y
  // de un sufijo razonable. Las URLs firmadas no llevan filename legible,
  // así que usamos `tarea<id>-<idx>` y dejamos que el helper agregue la
  // extensión según el MIME del blob.
  const downloadOne = async (url: string, index: number) => {
    if (!currentTask) return;
    setDownloadingUrl(url);
    try {
      const item = images.find((it) => it.url === url);
      const name = item?.originalName || `tarea${currentTask.id}-${index + 1}`;
      await downloadFile(url, name);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo descargar el archivo");
    } finally {
      setDownloadingUrl(null);
    }
  };

  const downloadAll = async () => {
    if (!currentTask || images.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadFilesAsZip(
        images.map((it, i) => ({
          url: it.url,
          name: it.originalName || `tarea${currentTask.id}-${i + 1}`,
        })),
        `tarea${currentTask.id}-archivos`,
      );
      getMsg()?.success(`${images.length} archivos empaquetados en ZIP`);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo armar el ZIP");
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDeleteImage = async (url: string) => {
    if (!currentTask) return;

    const prev = images;
    setImages(prev.filter((it) => it.url !== url)); // optimista

    try {
      const updatedUrls = await store.deleteImage(currentTask.id, url);
      // El backend devuelve array de strings; mapeamos preservando el shape rico
      // de los items originales cuando coinciden por url.
      setImages(
        updatedUrls.map((u) => prev.find((it) => it.url === u) || { url: u }),
      );
    } catch (e: any) {
      setImages(prev);
      getMsg()?.error(e?.message || "No se pudo eliminar la imagen");
      throw e;
    }
  };

  // Recibe el dataUrl YA comprimido por ImageEditorModal (compressImage
  // se ejecuta dentro del modal). Lo convierte a File y lo sube como
  // cualquier otro archivo. No vuelve a comprimir.
  const handleEditorSave = async (compressedDataUrl: string) => {
    if (!currentTask) return;
    try {
      setSaving(true);
      const file = dataUrlToFile(compressedDataUrl, `tarea${currentTask.id}-edit-${Date.now()}`);
      const newItem = await store.uploadMediaFile(currentTask.id, file);
      setImages((prev) => [...prev, newItem]);
      getMsg()?.success("Imagen agregada");
      setIsEditorVisible(false);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error guardando imagen");
    } finally {
      setSaving(false);
    }
  };

  const closeImagesModal = () => {
    setLbOpen(false);
    setLbIndex(0);
    setIsEditorVisible(false);
    setIsImageModalVisible(false);
    setImages([]);
    setImagesLoading(false);
    store.refreshTasks();
  };

  return (
    <ConfigProvider locale={esES}>
      <Layout
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        containerClass="home-container"
        contentClass="home-content"
      >
        <div className="tasks-page">
          <PageHeader
            title="Tareas"
            helpText="Organiza tareas por espacio y categoría. Asigna trabajadores y sigue el estado. Las tareas son SOLO informativas — no afectan ningún flujo financiero (ni de la empresa ni del cliente ni del aseador)."
          />

          {/* Round 2026-04-30 octies — banner explícito para admin/superadmin
              aclarando que /tareas no impacta ninguna finanza. Confusión
              recurrente: el admin pensaba que asignar un trabajador a una
              tarea generaba un movimiento contable. NO. Las tareas son un
              tablero de pendientes; los flujos de dinero viven en
              /finanzas-empresa, /finanzas-clientes y /finanzas-aseador-admin.
              El cliente no ve este banner — para él las tareas siempre fueron
              solo lectura, no genera dudas. */}
          {isAdmin && (
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              closable
              style={{ marginBottom: 12 }}
              message="Las tareas no afectan ninguna finanza"
              description={
                <div style={{ fontSize: 13 }}>
                  Este módulo es <strong>solo informativo</strong>: registrar,
                  asignar y seguir tareas/mantenciones. No genera ingresos ni
                  egresos en <code>/finanzas-empresa</code>,{" "}
                  <code>/finanzas-clientes</code> ni{" "}
                  <code>/finanzas-aseador-admin</code>. Si una tarea tiene
                  costo de materiales o sueldo, registralo aparte como gasto
                  en <code>/finanzas-empresa → Gastos</code>.
                </div>
              }
            />
          )}

          {isCliente && !loading && espaciosVisibles.length === 0 ? (
            <div className="tasks-empty-client">
              <div className="tasks-empty-client-icon">📋</div>
              <p className="tasks-empty-client-text">
                No hay ninguna tarea asociada a su(s) propiedad(es).
              </p>
            </div>
          ) : (
            <div
              className={`tasks-layout ${
                sidebarCollapsed ? "sidebar-collapsed" : ""
              }`}
            >
              <aside
                className={`tasks-sidebar ${
                  sidebarCollapsed ? "is-collapsed" : ""
                }`}
              >
                {sidebarCollapsed && (
                  <div className="sidebar-rail">
                    <Tooltip title="Expandir barra lateral" placement="right">
                      <button
                        className="rail-expand-btn"
                        onClick={() => setSidebarCollapsed(false)}
                      >
                        »
                      </button>
                    </Tooltip>

                    {/* Sección Spaces */}
                    <div className="rail-section">
                      {espaciosVisibles.map((s) => {
                        const active = s.id === store.activeSpaceId;
                        const initial = (s.name?.[0] ?? "S").toUpperCase();

                        return (
                          <Tooltip key={s.id} title={s.name} placement="right">
                            <button
                              className={`rail-item ${active ? "active" : ""}`}
                              onClick={() => {
                                store.setActiveSpaceId(s.id);
                                if (s.mode === "board") {
                                  const firstCat =
                                    s.categories?.[0]?.id ?? null;
                                  store.setActiveCategoryId(firstCat);
                                } else {
                                  store.setActiveCategoryId(null);
                                }
                              }}
                            >
                              <span className="rail-avatar">{initial}</span>
                            </button>
                          </Tooltip>
                        );
                      })}
                    </div>

                    <div className="rail-bottom">
                      <Tooltip title="Crear space" placement="right">
                        <button
                          className="rail-icon-btn"
                          onClick={() => {
                            setSidebarCollapsed(false);
                            setCreateOpen(true);
                          }}
                        >
                          +
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                )}

                {!sidebarCollapsed && (
                  <div className="sidebar-card sidebar-unified">
                    {/* Header */}
                    <div className="spaces-header">
                      <div className="spaces-title">Espacios</div>

                      <div className="spaces-actions">
                        {isAdmin && (
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => setCreateOpen((v) => !v)}
                          >
                            + Nuevo
                          </Button>
                        )}
                        <button
                          className="sidebar-collapse-btn"
                          onClick={() => setSidebarCollapsed(true)}
                          title="Colapsar barra lateral"
                        >
                          «
                        </button>
                      </div>
                    </div>

                    {createOpen && (
                      <div className="create-space-card">
                        <Input
                          placeholder="Nombre del espacio"
                          value={createName}
                          onChange={(e) => setCreateName(e.target.value)}
                          style={{ width: "100%" }}
                        />

                        {/*
                          Sectores vinculados al espacio (cero o muchos). Si
                          hay al menos uno, las propiedades elegibles para
                          las tareas dentro del espacio se restringen a las
                          de esos sectores. Si no se elige ninguno, el
                          espacio es "global" y muestra todas las
                          propiedades — válido por pedido del usuario
                          (round 2026-05-05).
                        */}
                        <Select
                          mode="multiple"
                          placeholder="Vincular a sectores (opcional, ninguno o varios)"
                          value={createSectorIds}
                          onChange={(v) => setCreateSectorIds(v as string[])}
                          options={sectorOptions}
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          style={{ width: "100%", marginTop: 8 }}
                        />

                        {createTemplate === "custom" && (
                          <div className="create-space-columns">
                            <div className="create-space-columns-title">
                              Columnas
                            </div>
                            <Checkbox.Group
                              value={selectedCols}
                              onChange={(vals) =>
                                setSelectedCols(vals as string[])
                              }
                              options={allColumnsOptions}
                            />
                          </div>
                        )}

                        <div className="create-space-actions">
                          <Button
                            type="primary"
                            onClick={async () => {
                              await createSpaceNow();
                              setCreateOpen(false);
                            }}
                            disabled={!createName}
                          >
                            Crear
                          </Button>
                          <Button
                            type="link"
                            onClick={() => {
                              setCreateOpen(false);
                              setCreateName("");
                              setCreateSectorIds([]);
                              templateChanged("default");
                            }}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                    {/* Lista de spaces */}
                    <div className="sidebar-list">
                      {espaciosVisibles.map((s) => {
                        const active = s.id === store.activeSpaceId;
                        const isEditing = editingSpaceId === s.id;

                        return (
                          <div
                            key={s.id}
                            className={`sidebar-item editable ${
                              active ? "active" : ""
                            }`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 6,
                              padding: "6px 8px",
                              borderRadius: 8,
                              background: active ? "#dbeafe" : "transparent",
                            }}
                            onClick={() => {
                              store.setActiveSpaceId(s.id);
                              if (s.mode === "board") {
                                store.setActiveCategoryId(
                                  s.categories?.[0]?.id ?? null,
                                );
                              } else {
                                store.setActiveCategoryId(null);
                              }
                            }}
                            onDoubleClick={() => {
                              if (!isAdmin) return;
                              if (!s.isSystem) {
                                setEditingSpaceId(s.id);
                                setEditingName(s.name);
                              }
                            }}
                          >
                            {isEditing && isAdmin ? (
                              <Input
                                autoFocus
                                size="small"
                                className="space-rename-input"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onBlur={async () => {
                                  const trimmed = editingName.trim();
                                  if (trimmed && trimmed !== s.name) {
                                    // Rechazo client-side de nombre
                                    // duplicado (case-insensitive). El
                                    // backend igual valida; este check
                                    // evita el toast de error 409 cuando
                                    // ya tenemos el dato local.
                                    const lower = trimmed.toLowerCase();
                                    const dup = (db?.spaces ?? []).some(
                                      (sp) =>
                                        sp.id !== s.id &&
                                        sp.name.toLowerCase() === lower,
                                    );
                                    if (dup) {
                                      getMsg()?.error(
                                        `Ya existe un espacio llamado "${trimmed}"`,
                                      );
                                      setEditingSpaceId(null);
                                      return;
                                    }
                                    await store.createSpace({
                                      id: s.id,
                                      name: trimmed,
                                    });
                                  }
                                  setEditingSpaceId(null);
                                }}
                                onPressEnter={(e) => e.currentTarget.blur()}
                              />
                            ) : (
                              <>
                                <span style={{ flex: 1 }}>{s.name}</span>
                                {!s.isSystem && isAdmin && (
                                  <>
                                    <span
                                      className="space-edit-icon"
                                      title="Renombrar espacio"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingSpaceId(s.id);
                                        setEditingName(s.name);
                                      }}
                                    >
                                      <EditOutlined />
                                    </span>
                                    {/*
                                      Botón eliminar espacio (admin only,
                                      no aparece en system spaces). Doble
                                      confirmación: askDeleteSpace lanza
                                      Modal.confirm encadenados — uno para
                                      la intención y otro para asumir el
                                      borrado de tareas dentro.
                                    */}
                                    <Tooltip title="Eliminar espacio">
                                      <span
                                        className="space-edit-icon space-edit-icon--danger"
                                        style={{
                                          marginLeft: 4,
                                          cursor: deletingSpace ? "wait" : "pointer",
                                          opacity: deletingSpace ? 0.4 : undefined,
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (deletingSpace) return;
                                          askDeleteSpace({
                                            id: s.id,
                                            name: s.name,
                                            isSystem: s.isSystem,
                                          });
                                        }}
                                      >
                                        <DeleteOutlined />
                                      </span>
                                    </Tooltip>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {activeSpace?.mode === "board" && (
                      <>
                        <div className="sidebar-divider" />
                        <div className="sidebar-title">Categorías</div>
                        {isAdmin && (
                          <div className="sidebar-create">
                            <Input
                              placeholder="Nueva categoría..."
                              value={newCategoryName}
                              style={{ paddingBottom: "5px" }}
                              onChange={(e) =>
                                setNewCategoryName(e.target.value)
                              }
                              onPressEnter={() => {
                                const n = newCategoryName.trim();
                                if (!n || !activeSpace) return;
                                store.createCategory(
                                  activeSpace.id,
                                  n,
                                  ALL_COLUMNS,
                                );
                                setNewCategoryName("");
                              }}
                            />
                            <Button
                              type="primary"
                              style={{ gap: "500px" }}
                              onClick={() => {
                                const n = newCategoryName.trim();
                                if (!n || !activeSpace) return;
                                store.createCategory(
                                  activeSpace.id,
                                  n,
                                  ALL_COLUMNS,
                                );
                                setNewCategoryName("");
                              }}
                            >
                              Añadir
                            </Button>
                          </div>
                        )}
                        <div className="sidebar-list">
                          {(activeSpace.categories ?? []).map((c) => {
                            const active = c.id === store.activeCategoryId;
                            const isEditingCat =
                              editingCategoryId === String(c.id);
                            return (
                              <div
                                key={c.id}
                                className={`sidebar-item editable ${
                                  active ? "active" : ""
                                }`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 8px",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  if (isEditingCat) return;
                                  store.setActiveCategoryId(c.id);
                                }}
                                onDoubleClick={() => {
                                  if (!isAdmin) return;
                                  setEditingCategoryId(String(c.id));
                                  setEditingCategoryName(c.name);
                                }}
                              >
                                <span className="sidebar-item-dot cat" />
                                {isEditingCat && isAdmin ? (
                                  <Input
                                    autoFocus
                                    size="small"
                                    value={editingCategoryName}
                                    onChange={(e) =>
                                      setEditingCategoryName(e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    onBlur={() =>
                                      commitCategoryRename(String(c.id), c.name)
                                    }
                                    onPressEnter={(e) =>
                                      e.currentTarget.blur()
                                    }
                                    style={{ flex: 1 }}
                                  />
                                ) : (
                                  <>
                                    <span
                                      className="sidebar-item-text"
                                      style={{
                                        flex: 1,
                                        opacity:
                                          renamingCategoryId === String(c.id)
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      {c.name}
                                    </span>
                                    {/*
                                      Spinner mientras el PATCH del rename
                                      está en vuelo — feedback visual de
                                      que la edición se está guardando.
                                    */}
                                    {renamingCategoryId === String(c.id) && (
                                      <Spin size="small" />
                                    )}
                                    {isAdmin && (
                                      <>
                                        {/*
                                          Lápiz: rename inline. Doble click
                                          en el item también funciona.
                                        */}
                                        <Tooltip title="Renombrar categoría">
                                          <span
                                            className="space-edit-icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingCategoryId(
                                                String(c.id),
                                              );
                                              setEditingCategoryName(c.name);
                                            }}
                                          >
                                            <EditOutlined />
                                          </span>
                                        </Tooltip>
                                        {/*
                                          Papelera: borra la categoría con
                                          confirmación (askDeleteCategory).
                                          Las tareas dentro NO se borran,
                                          quedan sin categoría asignada.
                                        */}
                                        <Tooltip title="Eliminar categoría">
                                          <span
                                            className="space-edit-icon space-edit-icon--danger"
                                            style={{ marginLeft: 2 }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              askDeleteCategory({
                                                id: String(c.id),
                                                name: c.name,
                                              });
                                            }}
                                          >
                                            <DeleteOutlined />
                                          </span>
                                        </Tooltip>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="sidebar-divider" />
                        {isAdmin && (
                          <>
                            <div className="sidebar-title">Configuración</div>
                            <div
                              className="sidebar-subtitle"
                              style={{ marginTop: 10 }}
                            >
                              Colores de propiedades
                            </div>
                            <Button
                              size="small"
                              onClick={() => setPropDrawerOpen(true)}
                            >
                              Administrar propiedades
                            </Button>
                            <ManageColor
                              open={propDrawerOpen}
                              onClose={() => setPropDrawerOpen(false)}
                              propiedades={propiedades}
                              onUpdateColor={(id, color) =>
                                store.updatePropertyColor(id, color)
                              }
                              onAutoAssign={() =>
                                store.autoAssignPropertyColors()
                              }
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </aside>

              <main className="tasks-main">
                <div className="table-card">
                  <div className="table-head">
                    <div>
                      <div className="table-title">
                        {activeSpace?.name ?? "Sin space"}{" "}
                        <span className="muted">/</span>{" "}
                        {activeSpace?.mode === "board"
                          ? (activeCategory?.name ?? "Sin categoría")
                          : "Lista única"}
                      </div>

                      {isAdmin && (
                        <>
                          <div className="table-subtitle">
                            Tip: completa la primera fila vacía de la tabla
                            para crear una tarea (la creación es inline).
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              flexWrap: "wrap",
                              alignItems: "center",
                              marginTop: 10,
                            }}
                          >
                            {/*
                              Round 2026-05-07: removido el botón "+ Nueva
                              tarea" porque era redundante — la creación
                              ya es inline en la primera fila vacía de la
                              tabla. Mantener un botón paralelo confundía
                              y daba la falsa expectativa de un modal.
                            */}

                            {/*
                              Botón de alertas — abre el drawer global con
                              tareas vencidas y recurrencias próximas.
                              Badge rojo con el conteo cuando hay items.
                              Disponible para todos los roles; el backend
                              filtra por permisos.
                            */}
                            <Badge count={alertasTotal} size="small">
                              <Button
                                icon={<BellOutlined />}
                                onClick={() => setAlertasOpen(true)}
                              >
                                Alertas
                              </Button>
                            </Badge>

                            <Input
                              allowClear
                              value={fDesc}
                              onChange={(e) => setFDesc(e.target.value)}
                              placeholder="Buscar por descripción…"
                              prefix={
                                <SearchOutlined style={{ color: "#bfbfbf" }} />
                              }
                              style={{ width: 320 }}
                            />

                            {activeSpace?.mode === "board" && (
                              <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                value={fPropIds}
                                onChange={(vals) => setFPropIds(vals)}
                                placeholder="Filtrar por propiedades…"
                                style={{ minWidth: 340 }}
                                options={propiedadOptions}
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                  norm((option as any)?.label).includes(
                                    norm(input),
                                  )
                                }
                                maxTagCount="responsive"
                              />
                            )}

                            {/*
                              Filtros adicionales (round 2026-05-06): solo
                              aplican a board mode (Papudo/Concón/etc).
                              Consultas/Gerencia (list mode) no usan
                              estado/prioridad/aseador como columnas, asi
                              que los ocultamos para no confundir.
                            */}
                            {activeSpace?.mode === "board" && (
                              <>
                                <Select
                                  mode="multiple"
                                  allowClear
                                  value={fEstado}
                                  onChange={(vals) =>
                                    setFEstado(vals as string[])
                                  }
                                  placeholder="Estado"
                                  style={{ minWidth: 180 }}
                                  options={ESTADOS_SECTOR.map((e) => ({
                                    value: e.value,
                                    label: e.label,
                                  }))}
                                  maxTagCount="responsive"
                                />
                                <Select
                                  mode="multiple"
                                  allowClear
                                  value={fPrioridad}
                                  onChange={(vals) =>
                                    setFPrioridad(vals as string[])
                                  }
                                  placeholder="Prioridad"
                                  style={{ minWidth: 160 }}
                                  options={PRIORIDADES_TAREA.map((p) => ({
                                    value: p.value,
                                    label: p.label,
                                  }))}
                                  maxTagCount="responsive"
                                />
                                <Select
                                  mode="multiple"
                                  allowClear
                                  showSearch
                                  value={fTrabajador}
                                  onChange={(vals) =>
                                    setFTrabajador(vals as string[])
                                  }
                                  placeholder="Aseador"
                                  style={{ minWidth: 180 }}
                                  options={aseadorOptions}
                                  optionFilterProp="label"
                                  maxTagCount="responsive"
                                />
                                {/*
                                  Rango de vencimiento (terminadoEl). Tareas
                                  sin terminadoEl quedan fuera del rango.
                                */}
                                <DatePicker.RangePicker
                                  value={fVenceRange as any}
                                  onChange={(vals) =>
                                    setFVenceRange(vals as any)
                                  }
                                  format="DD/MM/YY"
                                  placeholder={["Vence desde", "hasta"]}
                                  allowEmpty={[true, true]}
                                />
                              </>
                            )}

                            <Badge count={activeFilterCount} size="small">
                              <Button
                                onClick={clearAllFilters}
                                disabled={activeFilterCount === 0}
                              >
                                Limpiar
                              </Button>
                            </Badge>

                            <span
                              style={{
                                color: "rgba(0,0,0,0.55)",
                                fontSize: 12,
                              }}
                            >
                              {filteredTasks.length} resultado(s)
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <ImagesGalleryModal
                    open={isImageModalVisible}
                    onClose={closeImagesModal}
                    images={images}
                    setImages={setImages as any}
                    loading={imagesLoading}
                    isAdmin={isAdmin}
                    saving={saving}
                    currentTaskId={currentTask?.id}
                    lbOpen={lbOpen}
                    lbIndex={lbIndex}
                    setLbOpen={setLbOpen}
                    setLbIndex={setLbIndex}
                    currentSlide={currentSlide}
                    setCurrentSlide={setCurrentSlide}
                    downloadingAll={downloadingAll}
                    downloadingUrl={downloadingUrl}
                    deletingUrl={deletingUrl}
                    mutingKey={mutingKey}
                    setMutingKey={setMutingKey}
                    setDeletingUrl={setDeletingUrl}
                    downloadAll={downloadAll}
                    downloadOne={downloadOne}
                    uploadDirect={uploadDirect}
                    handleDeleteImage={handleDeleteImage}
                    setIsEditorVisible={setIsEditorVisible}
                  />

                  <ImageEditorModal
                    open={isEditorVisible}
                    onClose={() => setIsEditorVisible(false)}
                    onSave={handleEditorSave}
                    saving={saving}
                  />

                  {/* Modal de recurrencia (round 2026-05-06) — admin only */}
                  <RecurrenciaModal
                    open={recurrenciaTask !== null}
                    onClose={() => setRecurrenciaTask(null)}
                    current={recurrenciaTask?.recurrencia ?? null}
                    onSave={handleSaveRecurrencia}
                    saving={recurrenciaSaving}
                  />

                  {/* Drawer de alertas globales (round 2026-05-06) */}
                  <AlertasDrawer
                    open={alertasOpen}
                    onClose={() => setAlertasOpen(false)}
                    vencidas={alertasVencidas}
                    recurrentesProximas={alertasRecurrentes}
                    loading={alertasLoading}
                    onSelectTask={(t) => {
                      // Navegar al space/categoría de la tarea seleccionada
                      // y cerrar el drawer. setActiveSpaceId/CategoryId
                      // dispara refreshTasks via useEffect.
                      const sp = (t as any).spaceId;
                      const cat = (t as any).categoryId;
                      if (sp) store.setActiveSpaceId(sp);
                      if (cat) store.setActiveCategoryId(cat);
                      setAlertasOpen(false);
                    }}
                  />

                  {/* ── Documents modal ─────────────────────────────── */}
                  <DocsModal
                    taskId={docsModalTaskId}
                    docs={docs}
                    loading={docsLoading}
                    isAdmin={isAdmin}
                    onClose={closeDocsModal}
                    onUpload={handleUploadDocument}
                    onDelete={handleDeleteDocument}
                    setDocs={setDocs}
                  />
                  {activeSpace?.mode === "board" &&
                  !(activeSpace.categories ?? []).length ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "64px 24px",
                        color: "rgba(0,0,0,0.45)",
                      }}
                    >
                      <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                      <div style={{ fontSize: 16, marginBottom: 8 }}>
                        Creá una categoría para comenzar a agregar tareas
                      </div>
                      <div style={{ fontSize: 13, color: "rgba(0,0,0,0.35)" }}>
                        Usá el panel lateral para crear tu primera categoría
                      </div>
                    </div>
                  ) : (
                    <>
                      {/*
                        Barra contextual de borrado masivo. Solo visible para
                        admin/superadmin con al menos una tarea seleccionada.
                        El borrado individual y el masivo comparten endpoint
                        (`DELETE /api/tareas/:id`); el masivo simplemente
                        despacha en paralelo (no hay endpoint bulk en backend).
                      */}
                      {isAdmin && selectedRowKeys.length > 0 && (
                        <div
                          style={{
                            marginBottom: 12,
                            padding: "8px 12px",
                            background: "#fff1f0",
                            border: "1px solid #ffa39e",
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span>
                            <strong>{selectedRowKeys.length}</strong> tarea
                            {selectedRowKeys.length === 1 ? "" : "s"}{" "}
                            seleccionada
                            {selectedRowKeys.length === 1 ? "" : "s"}
                          </span>
                          <Space>
                            <Button
                              onClick={() => setSelectedRowKeys([])}
                              disabled={bulkDeleting || bulkMoving}
                            >
                              Limpiar selección
                            </Button>
                            {/*
                              Mover seleccionadas a otra categoria del MISMO
                              space (ej: de "Tareas" a "Reparaciones"). Solo
                              tiene sentido en modo "board"; el modo "list"
                              no usa categorias. Filtramos la categoria
                              actual del select para evitar mover a si misma.
                            */}
                            {activeSpace?.mode === "board" &&
                              (activeSpace?.categories ?? []).length > 1 && (
                                <Popover
                                  open={bulkMovePopoverOpen}
                                  onOpenChange={(o) => {
                                    setBulkMovePopoverOpen(o);
                                    if (!o) setBulkMoveTarget(null);
                                  }}
                                  trigger="click"
                                  placement="bottomRight"
                                  title={`Mover ${selectedRowKeys.length} tarea${selectedRowKeys.length === 1 ? "" : "s"} a...`}
                                  content={
                                    <Space
                                      direction="vertical"
                                      style={{ minWidth: 240 }}
                                    >
                                      <Select
                                        placeholder="Elegir categoria destino"
                                        style={{ width: "100%" }}
                                        value={bulkMoveTarget ?? undefined}
                                        onChange={(v) =>
                                          setBulkMoveTarget(v ?? null)
                                        }
                                        options={(
                                          activeSpace?.categories ?? []
                                        )
                                          .filter(
                                            (c: any) =>
                                              String(c.id) !==
                                              String(activeCategory?.id),
                                          )
                                          .map((c: any) => ({
                                            value: String(c.id),
                                            label: c.name,
                                          }))}
                                      />
                                      <Space>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setBulkMovePopoverOpen(false);
                                            setBulkMoveTarget(null);
                                          }}
                                          disabled={bulkMoving}
                                        >
                                          Cancelar
                                        </Button>
                                        <Button
                                          size="small"
                                          type="primary"
                                          loading={bulkMoving}
                                          disabled={!bulkMoveTarget}
                                          onClick={handleBulkMove}
                                        >
                                          Mover
                                        </Button>
                                      </Space>
                                    </Space>
                                  }
                                >
                                  <Button
                                    icon={<SwapOutlined />}
                                    disabled={bulkDeleting}
                                    loading={bulkMoving}
                                  >
                                    Mover a...
                                  </Button>
                                </Popover>
                              )}
                            <Popconfirm
                              title={`Eliminar ${selectedRowKeys.length} tarea${selectedRowKeys.length === 1 ? "" : "s"}`}
                              description="Esta acción es irreversible. Las tareas y sus adjuntos asociados se eliminarán."
                              okText="Eliminar"
                              cancelText="Cancelar"
                              okButtonProps={{
                                danger: true,
                                loading: bulkDeleting,
                              }}
                              onConfirm={handleBulkDelete}
                            >
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                loading={bulkDeleting}
                                disabled={bulkMoving}
                              >
                                Eliminar seleccionadas
                              </Button>
                            </Popconfirm>
                          </Space>
                        </div>
                      )}
                      <Table
                        rowKey={(r) => r.id}
                        loading={loading}
                        columns={columns}
                        dataSource={dataSource}
                        // PERF (round 2026-07-02): paginación client-side. Sin
                        // esto se montaban TODAS las filas del espacio a la vez,
                        // y cada fila monta ~7 widgets AntD pesados (Selects +
                        // DatePickers + Input numérico) → costo dominante de
                        // carga en boards grandes. 50 por página. `hideOnSinglePage`
                        // deja los boards chicos EXACTAMENTE igual que antes
                        // (scroll único, sin paginador); solo los grandes
                        // paginan. La fila draft va prepend y queda en página 1.
                        pagination={paginacionCliente({
                          porDefecto: 50,
                          opciones: [50, 100, 200],
                          etiqueta: "tarea",
                          ocultarSiUnaPagina: true,
                          size: "small",
                        })}
                        size="middle"
                        scroll={{ x: "max-content" }}
                        className="tasks-table"
                        // Filas vencidas: terminadoEl pasado y estado distinto
                        // de finalizado. Excluye la fila draft. Aplica una
                        // clase CSS para que el usuario detecte tareas
                        // atrasadas a primera vista (feedback visual
                        // permanente; el detalle vive en el drawer de Alertas).
                        rowClassName={(record) => {
                          if ((record as DraftRow).__draft) return "";
                          const r = record as any;
                          // Fila optimista en vuelo: atenuada hasta confirmar.
                          if (r._saving) return "task-row-saving";
                          const v = r.terminadoEl as string | null | undefined;
                          if (!v) return "";
                          if (v >= todayStr) return "";
                          if (String(r.estado ?? "") === "finalizado") return "";
                          return "task-row-vencida";
                        }}
                        // Solo admin ve checkboxes. La fila "draft" (creación
                        // inline) queda con checkbox deshabilitado para que no
                        // se pueda "seleccionar para eliminar" algo que aún no
                        // existe en backend. `preserveSelectedRowKeys` mantiene
                        // la selección si filtras la tabla y la fila se oculta.
                        rowSelection={
                          isAdmin
                            ? {
                                selectedRowKeys,
                                onChange: (keys) => setSelectedRowKeys(keys),
                                preserveSelectedRowKeys: true,
                                getCheckboxProps: (record) => ({
                                  // Deshabilita la fila draft y las optimistas
                                  // en vuelo (id temporal, aún no existen en
                                  // backend).
                                  disabled:
                                    !!(record as DraftRow).__draft ||
                                    !!(record as any)._saving,
                                }),
                              }
                            : undefined
                        }
                        onRow={(record) => {
                          if (!(record as DraftRow).__draft) return {};
                          return {
                            ref: draftRowRef,
                            onBlur: () => {
                              if (draftBlurTimer.current)
                                clearTimeout(draftBlurTimer.current);
                              draftBlurTimer.current = setTimeout(() => {
                                // No commitear si el foco sigue dentro de la
                                // fila, NI si hay un popup de AntD abierto
                                // (Select dropdown / DatePicker panel). Estos
                                // popups se portalean a `body`, asi q sacan el
                                // foco de la fila aunq el usuario siga
                                // interactuando con un widget de la draft.
                                // Sin esto, abrir el Select de Pri/Est o el
                                // DatePicker creaba la tarea con datos
                                // parciales y reseteaba el draft, generando
                                // multiples filas con campos sueltos.
                                const focusInside =
                                  draftRowRef.current?.contains(
                                    document.activeElement,
                                  );
                                const anyOpenPopup = !!document.querySelector(
                                  ".ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-picker-dropdown:not(.ant-picker-dropdown-hidden)",
                                );
                                if (focusInside || anyOpenPopup) return;
                                commitDraft();
                              }, 200);
                            },
                            onFocus: () => {
                              if (draftBlurTimer.current)
                                clearTimeout(draftBlurTimer.current);
                            },
                          };
                        }}
                      />
                    </>
                  )}
                </div>

                {/* Mini footer info */}
                <div className="legend"></div>
              </main>
            </div>
          )}
        </div>
      </Layout>
    </ConfigProvider>
  );
}
